import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import pusher from '../services/pusher';
import { Helmet } from 'react-helmet';
import AdminPanelProductTable from './AdminPanelProductTable';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper function to format email (truncate if more than 10 chars)
const formatEmail = (email) => {
  if (!email) return 'Unknown';
  if (email.length <= 10) return email;
  const [localPart, domain] = email.split('@');
  if (!domain) return email.substring(0, 10) + '...';
  const shortLocal = localPart.substring(0, 5);
  return `${shortLocal}...@${domain.length > 8 ? domain.substring(0, 8) + '...' : domain}`;
};

// Helper function to format source
const formatSource = (source) => {
  if (source === 'telegram') return 'TG';
  if (source === 'web') return 'WEB';
  if (source === 'guest') return 'GUEST';
  if (source === 'api') return 'API';
  return source?.toUpperCase() || 'N/A';
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [orders, setOrders] = useState([]);
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [vouchers, setVouchers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [newOrderNotification, setNewOrderNotification] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [orderToReject, setOrderToReject] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [orderToProcess, setOrderToProcess] = useState(null);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [orderVendorInfo, setOrderVendorInfo] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('boostlegends');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  
  const audioRef = useRef(null);
  const depositAudioRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  // Deposit states
  const [deposits, setDeposits] = useState([]);
  const [depositFilter, setDepositFilter] = useState('all');
  const [depositsCurrentPage, setDepositsCurrentPage] = useState(1);
  const [depositsPerPage] = useState(10);
  const [showRejectDepositModal, setShowRejectDepositModal] = useState(false);
  const [depositToReject, setDepositToReject] = useState(null);
  const [depositRejectReason, setDepositRejectReason] = useState('');
  const [newDepositNotification, setNewDepositNotification] = useState(false);
  const [showDepositDetailModal, setShowDepositDetailModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [hasViewedOrders, setHasViewedOrders] = useState(false);
  const [hasViewedDeposits, setHasViewedDeposits] = useState(false);
  
  // User detail states
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [userCustomPrices, setUserCustomPrices] = useState([]);
  const [customPriceForm, setCustomPriceForm] = useState({ reaction_type: 'rocket', custom_rate: 0.05 });
  
  // NOWPayments Status States
  const [nowpaymentsStatus, setNowpaymentsStatus] = useState(null);
  const [loadingNowpaymentsStatus, setLoadingNowpaymentsStatus] = useState(false);
  const [nowpaymentsError, setNowpaymentsError] = useState(null);
  
  // Export Report states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Reaction types for custom pricing
  const REACTION_TYPES = [
    { value: 'rocket', label: '🚀 Rocket', default_rate: 0.05 },
    { value: 'fire', label: '🔥 Fire', default_rate: 0.05 },
    { value: 'poop', label: '💩 Poop', default_rate: 0.05 },
    { value: 'redflag', label: '🚩 Red Flag', default_rate: 0.05 },
    { value: 'favorite', label: '⭐ Star', default_rate: 0.05 },
  ];
  
  const [newCredentials, setNewCredentials] = useState({
    new_username: '',
    new_password: ''
  });
  
  const [boostlegendsSettings, setBoostlegendsSettings] = useState({
    api_key: '',
    enabled: false,
    auto_fulfill: false
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [productMappings, setProductMappings] = useState([]);
  const [editingMapping, setEditingMapping] = useState(null);
  
  // Dexmoji Vendor Settings
  const [dexmojiSettings, setDexmojiSettings] = useState({
    api_key: '',
    enabled: false,
    auto_fulfill: false
  });
  const [testingDexmojiConnection, setTestingDexmojiConnection] = useState(false);
  const [dexmojiConnectionResult, setDexmojiConnectionResult] = useState(null);
  
  const [googleOAuthSettings, setGoogleOAuthSettings] = useState({
    client_id: '',
    client_secret: '',
    enabled: false
  });
  
  const [gtagSettings, setGtagSettings] = useState({
    gtag_code: '',
    enabled: false
  });
  
  const [telegramBotSettings, setTelegramBotSettings] = useState({
    bot_token: '',
    bot_username: '',
    webhook_url: '',
    enabled: false
  });
  const [testingTelegramBot, setTestingTelegramBot] = useState(false);
  const [telegramBotTestResult, setTelegramBotTestResult] = useState(null);
  
  const [fonnteSettings, setFonnteSettings] = useState({
    fonnte_token: '',
    admin_whatsapp_number: '081217460013',
    country_code: '62',
    enabled: true
  });
  const [testingFonnte, setTestingFonnte] = useState(false);
  const [fonnteTestResult, setFonnteTestResult] = useState(null);
  
  const [nowpaymentsSettings, setNowpaymentsSettings] = useState({
    api_key: '',
    ipn_secret: '',
    callback_url: '',
    sandbox_mode: false,
    enabled: false
  });
  const [testingNowpayments, setTestingNowpayments] = useState(false);
  const [nowpaymentsTestResult, setNowpaymentsTestResult] = useState(null);
  
  // SMTP Settings
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Dexreaction',
    smtp_use_tls: true,
    smtp_enabled: false
  });
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  
  // Database Backup & Restore
  const [backupInfo, setBackupInfo] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  
  const [banners, setBanners] = useState([]);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    desktop_image_url: '',
    mobile_image_url: '',
    link_href: '',
    enabled: true,
    order: 0
  });
  const [uploadingDesktop, setUploadingDesktop] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  
  const [voucherForm, setVoucherForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase: '0',
    max_uses: '',
    expires_at: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/system/admin');
      return;
    }
    fetchData();
    subscribeToAdminChannel();
    
    // Reset pagination when switching tabs
    if (activeTab === 'users') {
      setUsersCurrentPage(1);
    } else if (activeTab === 'orders') {
      setOrdersCurrentPage(1);
    }
  }, [activeTab]);

  // Reset orders pagination when filters change
  useEffect(() => {
    setOrdersCurrentPage(1);
  }, [searchQuery, filterStatus, filterUser, filterDate]);

  useEffect(() => {
    if (activeTicket) {
      fetchMessages(activeTicket.id);
      subscribeToTicket(activeTicket.id);
    }
  }, [activeTicket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch NOWPayments status when order detail modal is opened for a NOWPayments order
  useEffect(() => {
    let pollingInterval = null;
    
    const fetchNowpaymentsStatus = async () => {
      if (!selectedOrder || selectedOrder.payment_method !== 'nowpayments') {
        setNowpaymentsStatus(null);
        return;
      }
      
      setLoadingNowpaymentsStatus(true);
      setNowpaymentsError(null);
      
      try {
        const response = await adminAPI.getOrderNowpaymentsStatus(selectedOrder.id);
        setNowpaymentsStatus(response.data);
      } catch (error) {
        console.error('Error fetching NOWPayments status:', error);
        setNowpaymentsError(error.response?.data?.detail || 'Failed to fetch payment status');
      } finally {
        setLoadingNowpaymentsStatus(false);
      }
    };
    
    if (showOrderDetailModal && selectedOrder?.payment_method === 'nowpayments') {
      fetchNowpaymentsStatus();
      // Poll every 15 seconds for live status updates
      pollingInterval = setInterval(fetchNowpaymentsStatus, 15000);
    }
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      setNowpaymentsStatus(null);
      setNowpaymentsError(null);
    };
  }, [showOrderDetailModal, selectedOrder?.id, selectedOrder?.payment_method]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const subscribeToAdminChannel = () => {
    const channel = pusher.subscribe('admin-channel');
    
    channel.bind('new-order', (data) => {
      setNewOrderNotification(true);
      playNotificationSound();
      if (activeTab === 'orders') {
        fetchData();
      }
    });

    // New event: pending verification
    channel.bind('new-verification', (data) => {
      setNewOrderNotification(true);
      playNotificationSound();
      // Refresh orders list
      if (activeTab === 'orders') {
        fetchData();
      }
    });

    // New event: new deposit
    channel.bind('new-deposit', (data) => {
      setNewDepositNotification(true);
      playDepositNotificationSound();
      // Refresh deposits list
      if (activeTab === 'deposits') {
        fetchData();
      }
    });

    channel.bind('new-ticket', (data) => {
      // Play new message sound for new ticket
      const audio = new Audio('/notif-newmessage.wav');
      audio.play().catch(err => console.log('Audio play failed:', err));
      if (activeTab === 'support') {
        fetchData();
      }
    });

    channel.bind('new-message', (data) => {
      // Play new message sound
      const audio = new Audio('/notif-newmessage.wav');
      audio.play().catch(err => console.log('Audio play failed:', err));
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
  };

  const subscribeToTicket = (ticketId) => {
    const channel = pusher.subscribe(`ticket-${ticketId}`);
    
    channel.bind('new-message', (data) => {
      // Play sound for new message (admin perspective)
      const audio = new Audio('/notif-newmessage.wav');
      audio.play().catch(err => console.log('Audio play failed:', err));
      
      // Check for duplicates by ID
      setMessages((prev) => {
        const exists = prev.find(m => m.id === data.message.id);
        if (exists) return prev;
        return [...prev, data.message];
      });
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
  };

  const playNotificationSound = () => {
    if (!audioRef.current) {
      // Use uploaded audio file
      audioRef.current = new Audio('/notif-neworder.wav');
    }
    audioRef.current.loop = true;
    audioRef.current.play().catch(err => console.log('Audio play failed:', err));
  };

  const stopNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
    setNewOrderNotification(false);
  };

  const playDepositNotificationSound = () => {
    if (!depositAudioRef.current) {
      depositAudioRef.current = new Audio('/deposit-notification.mp3');
    }
    depositAudioRef.current.play().catch(err => console.log('Deposit audio play failed:', err));
  };

  const clearDepositNotification = () => {
    setNewDepositNotification(false);
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'stats') {
        const response = await adminAPI.getStats();
        setStats(response.data);
      } else if (activeTab === 'users') {
        const response = await adminAPI.getUsers();
        setUsers(response.data);
      } else if (activeTab === 'orders') {
        const [ordersResponse, mappingsResponse] = await Promise.all([
          adminAPI.getOrders(),
          adminAPI.getProductMappings()
        ]);
        setOrders(ordersResponse.data);
        setProductMappings(mappingsResponse.data);
        stopNotificationSound();
      } else if (activeTab === 'vouchers') {
        const response = await adminAPI.getVouchers();
        setVouchers(response.data);
      } else if (activeTab === 'support') {
        const response = await adminAPI.getTickets();
        setTickets(response.data);
      } else if (activeTab === 'deposits') {
        const response = await adminAPI.getDeposits();
        setDeposits(response.data);
        clearDepositNotification();
      } else if (activeTab === 'settings') {
        const [settingsResponse, mappingsResponse, googleOAuthResponse, gtagResponse, telegramBotResponse, dexmojiSettingsResponse, fonnteSettingsResponse, nowpaymentsSettingsResponse, bannersResponse, smtpSettingsResponse] = await Promise.all([
          adminAPI.getBoostlegendsSettings(),
          adminAPI.getProductMappings(),
          adminAPI.getGoogleOAuthSettings(),
          adminAPI.getGtagSettings(),
          adminAPI.getTelegramBotSettings(),
          adminAPI.getDexmojiSettings(),
          adminAPI.getFonnteSettings(),
          adminAPI.getNowpaymentsSettings(),
          adminAPI.getBanners(),
          adminAPI.getSmtpSettings()
        ]);
        setBoostlegendsSettings(settingsResponse.data);
        setProductMappings(mappingsResponse.data);
        setGoogleOAuthSettings(googleOAuthResponse.data);
        setGtagSettings(gtagResponse.data);
        setTelegramBotSettings(telegramBotResponse.data);
        setFonnteSettings(fonnteSettingsResponse.data);
        setNowpaymentsSettings(nowpaymentsSettingsResponse.data);
        setDexmojiSettings(dexmojiSettingsResponse.data);
        setBanners(bannersResponse.data);
        setSmtpSettings(smtpSettingsResponse.data);
        // Fetch backup info
        try {
          const backupInfoResponse = await adminAPI.getBackupInfo();
          setBackupInfo(backupInfoResponse.data);
        } catch (e) {
          console.error('Failed to fetch backup info:', e);
        }
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUsername');
        navigate('/system/admin');
      } else {
        setError('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (ticketId) => {
    try {
      const response = await adminAPI.getTicketMessages(ticketId);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleCompleteOrder = async (orderId) => {
    try {
      await adminAPI.completeOrder(orderId);
      fetchData();
    } catch (error) {
      alert('Failed to complete order');
    }
  };

  // User Detail Functions
  const handleViewUserDetail = async (user) => {
    setSelectedUser(user);
    setShowUserDetailModal(true);
    setLoadingUserDetail(true);
    setUserCustomPrices([]);
    try {
      const [detailRes, pricesRes] = await Promise.all([
        adminAPI.getUserDetail(user.id),
        adminAPI.getUserCustomPrices(user.id)
      ]);
      setUserDetail(detailRes.data);
      setUserCustomPrices(pricesRes.data.custom_prices || []);
    } catch (error) {
      console.error('Error fetching user detail:', error);
      alert('Failed to load user details');
    } finally {
      setLoadingUserDetail(false);
    }
  };
  
  const handleSetCustomPrice = async (userId) => {
    try {
      await adminAPI.setUserCustomPrice(userId, customPriceForm);
      alert(`Custom price set for ${customPriceForm.reaction_type}`);
      // Refresh custom prices
      const pricesRes = await adminAPI.getUserCustomPrices(userId);
      setUserCustomPrices(pricesRes.data.custom_prices || []);
    } catch (error) {
      alert('Failed to set custom price: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleDeleteCustomPrice = async (userId, reactionType) => {
    if (!window.confirm(`Delete custom price for ${reactionType}? User will revert to default price.`)) return;
    try {
      await adminAPI.deleteUserCustomPrice(userId, reactionType);
      alert('Custom price deleted');
      // Refresh custom prices
      const pricesRes = await adminAPI.getUserCustomPrices(userId);
      setUserCustomPrices(pricesRes.data.custom_prices || []);
    } catch (error) {
      alert('Failed to delete custom price: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Export Report Functions
  const handleFetchReport = async () => {
    setLoadingReport(true);
    try {
      const response = await adminAPI.getMonthlyReport(exportYear, exportMonth);
      setReportData(response.data);
    } catch (error) {
      alert('Failed to fetch report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingReport(false);
    }
  };

  const handleExportPDF = () => {
    if (!reportData) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(128, 90, 213); // Purple
    doc.text('Dexreaction Monthly Report', pageWidth / 2, 20, { align: 'center' });
    
    // Period
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Period: ${reportData.period.month_name} ${reportData.period.year}`, pageWidth / 2, 30, { align: 'center' });
    
    // Summary Section
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Summary Overview', 14, 45);
    
    const summaryData = [
      ['Total Orders', reportData.summary.total_orders.toString()],
      ['Total Reactions', reportData.summary.total_reactions.toLocaleString()],
      ['Total Revenue', `$${reportData.summary.total_revenue.toFixed(2)}`],
      ['Active Users', reportData.summary.total_users.toString()],
      ['Most Popular Reaction', `${reportData.summary.most_popular_reaction.type} (${reportData.summary.most_popular_reaction.count.toLocaleString()})`],
    ];
    
    autoTable(doc, {
      startY: 50,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'striped',
      headStyles: { fillColor: [128, 90, 213] },
      margin: { left: 14, right: 14 },
    });
    
    // Reaction Breakdown
    const lastY1 = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.text('Reaction Breakdown', 14, lastY1);
    
    const reactionData = Object.entries(reportData.summary.reaction_breakdown).map(([type, count]) => {
      const emoji = { rocket: '🚀', fire: '🔥', poop: '💩', redflag: '🚩', favorite: '⭐' };
      return [emoji[type] + ' ' + type.charAt(0).toUpperCase() + type.slice(1), count.toLocaleString()];
    });
    
    autoTable(doc, {
      startY: lastY1 + 5,
      head: [['Reaction Type', 'Total Purchased']],
      body: reactionData,
      theme: 'striped',
      headStyles: { fillColor: [128, 90, 213] },
      margin: { left: 14, right: 14 },
    });
    
    // User Overview Table - New Page
    doc.addPage();
    doc.setFontSize(14);
    doc.text('User Transaction Overview', 14, 20);
    
    const userData = reportData.users.slice(0, 50).map((user, index) => [
      (index + 1).toString(),
      user.email,
      user.full_name || '-',
      user.total_orders.toString(),
      user.total_reactions.toLocaleString(),
      `$${user.total_spent.toFixed(2)}`,
    ]);
    
    autoTable(doc, {
      startY: 25,
      head: [['#', 'Email', 'Name', 'Orders', 'Reactions', 'Spent']],
      body: userData,
      theme: 'striped',
      headStyles: { fillColor: [128, 90, 213] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 50 },
        2: { cellWidth: 35 },
        3: { cellWidth: 20 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
      },
    });
    
    // Transaction Details - New Page
    if (reportData.transactions && reportData.transactions.length > 0) {
      doc.addPage();
      doc.setFontSize(14);
      doc.text('Transaction Details (Latest 100)', 14, 20);
      
      const txData = reportData.transactions.slice(0, 100).map((tx, index) => {
        const date = new Date(tx.created_at).toLocaleDateString();
        return [
          (index + 1).toString(),
          tx.id?.slice(0, 8) + '...',
          tx.reaction_type,
          tx.quantity.toLocaleString(),
          `$${tx.total_usd?.toFixed(2) || '0.00'}`,
          tx.source || 'web',
          tx.status,
          date,
        ];
      });
      
      autoTable(doc, {
        startY: 25,
        head: [['#', 'Order ID', 'Type', 'Qty', 'Total', 'Source', 'Status', 'Date']],
        body: txData,
        theme: 'striped',
        headStyles: { fillColor: [128, 90, 213] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 7 },
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated by Dexreaction Admin Panel | Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Save PDF
    doc.save(`Dexreaction_Report_${reportData.period.month_name}_${reportData.period.year}.pdf`);
  };

  const handleBlockUser = async (userId) => {
    const action = userDetail?.user?.is_blocked ? 'unblock' : 'block';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    try {
      const response = await adminAPI.blockUser(userId);
      alert(response.data.message);
      // Refresh user detail
      const detailResponse = await adminAPI.getUserDetail(userId);
      setUserDetail(detailResponse.data);
      // Also refresh users list
      fetchData();
    } catch (error) {
      alert('Failed to update user status: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleApiKey = async (userId, keyId) => {
    try {
      const response = await adminAPI.toggleUserApiKey(userId, keyId);
      alert(response.data.message);
      // Refresh user detail
      const detailResponse = await adminAPI.getUserDetail(userId);
      setUserDetail(detailResponse.data);
    } catch (error) {
      alert('Failed to toggle API key: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteApiKey = async (userId, keyId) => {
    if (!window.confirm('Are you sure you want to terminate this API key? This action cannot be undone.')) return;
    
    try {
      const response = await adminAPI.deleteUserApiKey(userId, keyId);
      alert(response.data.message);
      // Refresh user detail
      const detailResponse = await adminAPI.getUserDetail(userId);
      setUserDetail(detailResponse.data);
    } catch (error) {
      alert('Failed to delete API key: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAcceptPayment = async (orderId) => {
    if (window.confirm('Accept this payment and move order to processing?')) {
      try {
        await adminAPI.acceptPayment(orderId);
        alert('Payment accepted! Order is now processing.');
        fetchData();
        setOpenDropdown(null);
      } catch (error) {
        alert('Failed to accept payment: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleRejectPayment = async (orderId) => {
    setOrderToReject(orderId);
    setShowRejectModal(true);
    setOpenDropdown(null);
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    try {
      await adminAPI.rejectPayment(orderToReject, { reason: rejectReason });
      alert('Payment rejected. Order marked as failed.');
      fetchData();
      setShowRejectModal(false);
      setRejectReason('');
      setOrderToReject(null);
    } catch (error) {
      alert('Failed to reject payment: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleProcessOrder = async (orderId) => {
    setOrderToProcess(orderId);
    
    // Get order details to determine default vendor and show reaction info
    const order = orders.find(o => o.id === orderId);
    if (order) {
      // Get reaction info
      const reaction = productMappings.find(r => r.id === order.reaction_type);
      if (reaction) {
        // Set default vendor from active_vendor config
        const defaultVendor = reaction.active_vendor || 'boostlegends';
        setSelectedVendor(defaultVendor);
        
        setOrderVendorInfo({
          vendor: defaultVendor,
          vendorName: defaultVendor === 'dexmoji' ? 'Dexmoji' : 'Boostlegends',
          reactionName: reaction.name,
          reactionEmoji: reaction.emoji,
          boostlegendsId: reaction.boostlegends_service_id || 0,
          dexmojiId: reaction.dexmoji_service_id || 0
        });
      }
    }
    
    setShowProcessModal(true);
    setOpenDropdown(null);
  };

  const handleProcessAuto = async () => {
    setProcessingOrder(true);
    try {
      // Pass selected vendor to API
      const response = await adminAPI.processOrderAuto(orderToProcess, selectedVendor);
      const vendorUsed = response.data.vendor_used || selectedVendor;
      const vendorOrderId = response.data.vendor_order_id;
      const vendorName = vendorUsed === 'dexmoji' ? 'Dexmoji' : 'Boostlegends';
      
      alert(`✅ ${response.data.message}\n\nVendor: ${vendorName}\nOrder ID: ${vendorOrderId}`);
      fetchData();
      setShowProcessModal(false);
      setOrderToProcess(null);
      setOrderVendorInfo(null);
      setSelectedVendor('boostlegends');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to process order';
      alert(`❌ Auto processing failed: ${errorMsg}\n\nPlease check vendor configuration or use manual processing instead.`);
      // Don't close modal, let admin choose manual instead
    } finally {
      setProcessingOrder(false);
    }
  };

  const handleProcessManual = async () => {
    setProcessingOrder(true);
    try {
      await adminAPI.processOrderManual(orderToProcess);
      alert('✅ Order set to processing (manual fulfillment).\n\nPlease complete the order manually, then click "Complete" when done.');
      fetchData();
      setShowProcessModal(false);
      setOrderToProcess(null);
      setOrderVendorInfo(null);
      setSelectedVendor('boostlegends');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to process order manually');
    } finally {
      setProcessingOrder(false);
    }
  };

  const handleDismissNotification = () => {
    setNewOrderNotification(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };


  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.updateCredentials(newCredentials);
      alert('Credentials updated successfully! Please login again.');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUsername');
      navigate('/system/admin');
    } catch (error) {
      alert('Failed to update credentials');
    }
  };

  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createVoucher({
        ...voucherForm,
        discount_value: parseFloat(voucherForm.discount_value),
        min_purchase: parseFloat(voucherForm.min_purchase),
        max_uses: voucherForm.max_uses ? parseInt(voucherForm.max_uses) : null,
        expires_at: voucherForm.expires_at || null
      });
      setShowVoucherModal(false);
      setVoucherForm({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        min_purchase: '0',
        max_uses: '',
        expires_at: ''
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create voucher');
    }
  };

  const handleToggleVoucher = async (voucherId, currentActive) => {
    try {
      await adminAPI.updateVoucher(voucherId, !currentActive);
      fetchData();
      alert(`Voucher ${!currentActive ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Failed to update voucher:', error);
      alert('Failed to update voucher: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteVoucher = async (voucherId) => {
    if (!window.confirm('Are you sure you want to delete this voucher?')) return;
    try {
      await adminAPI.deleteVoucher(voucherId);
      fetchData();
    } catch (error) {
      alert('Failed to delete voucher');
    }
  };

  const handleSaveBoostlegendsSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setConnectionResult(null);
    try {
      await adminAPI.updateBoostlegendsSettings(boostlegendsSettings);
      alert('Boostlegends settings saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoogleOAuthSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.updateGoogleOAuthSettings(googleOAuthSettings);
      alert('Google OAuth settings saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save Google OAuth settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGtagSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.updateGtagSettings(gtagSettings);
      alert('Google Tag settings saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save Google Tag settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTelegramBotSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTelegramBotTestResult(null);
    try {
      const response = await adminAPI.updateTelegramBotSettings(telegramBotSettings);
      
      // Show webhook status if available
      if (response.data.webhook_status) {
        const webhookStatus = response.data.webhook_status;
        if (webhookStatus.success) {
          alert(`✅ Telegram Bot settings saved successfully!\n\n${webhookStatus.message}`);
        } else {
          alert(`⚠️ Settings saved but webhook setup had an issue:\n\n${webhookStatus.message}\n\nPlease check your bot token and webhook URL.`);
        }
      } else {
        alert('✅ Telegram Bot settings saved successfully!');
      }
      
      // Set test result to show webhook status
      if (response.data.webhook_status) {
        setTelegramBotTestResult(response.data.webhook_status);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save Telegram Bot settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestTelegramBot = async () => {
    setTestingTelegramBot(true);
    setTelegramBotTestResult(null);
    try {
      const response = await adminAPI.testTelegramBot();
      setTelegramBotTestResult(response.data);
    } catch (error) {
      setTelegramBotTestResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to test connection'
      });
    } finally {
      setTestingTelegramBot(false);
    }
  };

  // Fonnte WhatsApp Handlers
  const handleSaveFonnteSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFonnteTestResult(null);
    try {
      await adminAPI.updateFonnteSettings(fonnteSettings);
      alert('✅ Fonnte WhatsApp settings saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save Fonnte settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestFonnteConnection = async () => {
    if (!fonnteSettings.fonnte_token) {
      alert('Please enter Fonnte API token first');
      return;
    }
    setTestingFonnte(true);
    setFonnteTestResult(null);
    try {
      const response = await adminAPI.testFonnteConnection();
      setFonnteTestResult(response.data);
    } catch (error) {
      setFonnteTestResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to test connection'
      });
    } finally {
      setTestingFonnte(false);
    }
  };

  // NOWPayments Handlers
  const handleSaveNowpaymentsSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNowpaymentsTestResult(null);
    try {
      await adminAPI.updateNowpaymentsSettings(nowpaymentsSettings);
      alert('✅ NOWPayments settings saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save NOWPayments settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestNowpaymentsConnection = async () => {
    if (!nowpaymentsSettings.api_key) {
      alert('Please enter NOWPayments API key first');
      return;
    }
    setTestingNowpayments(true);
    setNowpaymentsTestResult(null);
    try {
      const response = await adminAPI.testNowpaymentsConnection();
      setNowpaymentsTestResult(response.data);
    } catch (error) {
      setNowpaymentsTestResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to test connection'
      });
    } finally {
      setTestingNowpayments(false);
    }
  };

  // SMTP Settings Handlers
  const handleSaveSmtpSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSmtpTestResult(null);
    try {
      await adminAPI.updateSmtpSettings(smtpSettings);
      alert('✅ SMTP settings saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSmtpConnection = async () => {
    if (!smtpSettings.smtp_host || !smtpSettings.smtp_username) {
      alert('Please enter SMTP host and username first');
      return;
    }
    setTestingSmtp(true);
    setSmtpTestResult(null);
    try {
      const response = await adminAPI.testSmtpConnection();
      setSmtpTestResult(response.data);
    } catch (error) {
      setSmtpTestResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to test SMTP connection'
      });
    } finally {
      setTestingSmtp(false);
    }
  };

  // Database Backup & Restore Handlers
  const fetchBackupInfo = async () => {
    try {
      const response = await adminAPI.getBackupInfo();
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch backup info:', error);
    }
  };

  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await adminAPI.downloadBackup();
      
      // Create download link
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dexreaction_backup_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Backup downloaded successfully!');
    } catch (error) {
      alert('Failed to download backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (!restoreFile) {
      alert('Please select a backup file first');
      return;
    }

    if (!window.confirm('⚠️ WARNING: This will overwrite all existing data in the database. Are you sure you want to continue?')) {
      return;
    }

    setRestoreLoading(true);
    setRestoreResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      
      const response = await adminAPI.restoreBackup(formData);
      setRestoreResult(response.data);
      
      if (response.data.success) {
        alert('✅ Database restored successfully! Please refresh the page.');
        // Refresh backup info
        fetchBackupInfo();
      } else {
        alert('⚠️ Restore completed with errors. Check the results below.');
      }
    } catch (error) {
      setRestoreResult({
        success: false,
        errors: [error.response?.data?.detail || error.message]
      });
      alert('Failed to restore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRestoreLoading(false);
      setRestoreFile(null);
    }
  };

  // Banner Management Handlers
  const handleOpenBannerModal = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setBannerForm({
        title: banner.title,
        desktop_image_url: banner.desktop_image_url,
        mobile_image_url: banner.mobile_image_url,
        link_href: banner.link_href,
        enabled: banner.enabled,
        order: banner.order
      });
    } else {
      setEditingBanner(null);
      setBannerForm({
        title: '',
        desktop_image_url: '',
        mobile_image_url: '',
        link_href: '',
        enabled: true,
        order: banners.length
      });
    }
    setShowBannerModal(true);
  };

  const handleCloseBannerModal = () => {
    setShowBannerModal(false);
    setEditingBanner(null);
    setBannerForm({
      title: '',
      desktop_image_url: '',
      mobile_image_url: '',
      link_href: '',
      enabled: true,
      order: 0
    });
  };

  const handleUploadImage = async (file, type) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      if (type === 'desktop') setUploadingDesktop(true);
      else setUploadingMobile(true);
      
      const response = await adminAPI.uploadBannerImage(formData);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      if (type === 'desktop') {
        setBannerForm({ ...bannerForm, desktop_image_url: imageUrl });
      } else {
        setBannerForm({ ...bannerForm, mobile_image_url: imageUrl });
      }
      
      alert(`${type === 'desktop' ? 'Desktop' : 'Mobile'} image uploaded successfully!`);
    } catch (error) {
      alert(`Failed to upload ${type} image: ${error.response?.data?.detail || error.message}`);
    } finally {
      if (type === 'desktop') setUploadingDesktop(false);
      else setUploadingMobile(false);
    }
  };

  const handleSaveBanner = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingBanner) {
        await adminAPI.updateBanner(editingBanner.id, bannerForm);
        alert('Banner updated successfully!');
      } else {
        await adminAPI.createBanner(bannerForm);
        alert('Banner created successfully!');
      }
      
      // Refresh banners
      const response = await adminAPI.getBanners();
      setBanners(response.data);
      handleCloseBannerModal();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save banner');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    
    setLoading(true);
    try {
      await adminAPI.deleteBanner(bannerId);
      const response = await adminAPI.getBanners();
      setBanners(response.data);
      alert('Banner deleted successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete banner');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBanner = async (bannerId) => {
    setLoading(true);
    try {
      await adminAPI.toggleBanner(bannerId);
      const response = await adminAPI.getBanners();
      setBanners(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to toggle banner');
    } finally {
      setLoading(false);
    }
  };

  // Dexmoji Vendor Handlers
  const handleSaveDexmojiSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDexmojiConnectionResult(null);
    try {
      await adminAPI.updateDexmojiSettings(dexmojiSettings);
      alert('Dexmoji settings saved successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save Dexmoji settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestDexmojiConnection = async () => {
    if (!dexmojiSettings.api_key) {
      alert('Please enter an API key first');
      return;
    }
    
    setTestingDexmojiConnection(true);
    setDexmojiConnectionResult(null);
    
    try {
      const response = await adminAPI.testDexmojiConnection();
      setDexmojiConnectionResult({
        success: true,
        message: response.data.message || 'Connection successful!'
      });
    } catch (error) {
      setDexmojiConnectionResult({
        success: false,
        message: error.response?.data?.detail || 'Connection failed'
      });
    } finally {
      setTestingDexmojiConnection(false);
    }
  };

  const handleSaveProductMapping = async (mapping) => {
    try {
      await adminAPI.updateProductMapping(mapping);
      // Optionally show success message
      console.log('Product mapping saved:', mapping.id);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update product mapping');
    }
  };

  const handleTestConnection = async () => {
    if (!boostlegendsSettings.api_key) {
      alert('Please enter an API key first');
      return;
    }
    
    setTestingConnection(true);
    setConnectionResult(null);
    
    try {
      const response = await adminAPI.testBoostlegendsConnection();
      setConnectionResult({
        success: true,
        message: response.data.message,
        balance: response.data.balance,
        currency: response.data.currency
      });
    } catch (error) {
      setConnectionResult({
        success: false,
        message: error.response?.data?.detail || 'Connection failed'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleUpdateMapping = async (mapping) => {
    try {
      await adminAPI.updateProductMapping(mapping);
      alert(`Product mapping for ${mapping.name} updated successfully!`);
      setEditingMapping(null);
      // Refresh mappings
      const response = await adminAPI.getProductMappings();
      setProductMappings(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update mapping');
    }
  };

  const handleApproveDeposit = async (txId) => {
    if (!window.confirm('Are you sure you want to approve this deposit?')) {
      return;
    }
    
    try {
      await adminAPI.approveDeposit(txId);
      alert('Deposit approved successfully!');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve deposit');
    }
  };

  const handleRejectDeposit = (txId) => {
    setDepositToReject(txId);
    setShowRejectDepositModal(true);
  };

  const handleConfirmRejectDeposit = async () => {
    if (!depositRejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    try {
      await adminAPI.rejectDeposit(depositToReject, { reason: depositRejectReason });
      alert('Deposit rejected successfully');
      setShowRejectDepositModal(false);
      setDepositRejectReason('');
      setDepositToReject(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject deposit');
    }
  };


  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeTicket) return;

    try {
      const response = await adminAPI.sendMessage(activeTicket.id, { message: newMessage });
      setNewMessage('');
      // Don't add to state here - let Pusher handle it to avoid duplicates
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;
    try {
      await adminAPI.closeTicket(ticketId);
      await fetchData();
      if (activeTicket?.id === ticketId) {
        setActiveTicket(null);
      }
    } catch (error) {
      alert('Failed to close ticket');
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      await adminAPI.updateTicketStatus(ticketId, { status: newStatus });
      await fetchData();
      // Update active ticket if it's the one being modified
      if (activeTicket?.id === ticketId) {
        setActiveTicket({ ...activeTicket, status: newStatus });
      }
    } catch (error) {
      alert('Failed to update ticket status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    navigate('/system/admin');
  };

  const adminUsername = localStorage.getItem('adminUsername');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Admin Panel - Dexreaction</title>
      </Helmet>
      
      {newOrderNotification && (
        <div className="fixed top-4 right-4 z-50 bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-4 rounded-xl shadow-2xl border border-red-400">
          <div className="flex items-center justify-between space-x-4">
            <div className="flex items-center space-x-3">
              <div className="text-3xl animate-bounce">🔔</div>
              <div>
                <div className="font-bold text-lg">New Order Alert!</div>
                <div className="text-sm opacity-90">Click Orders tab to view details</div>
              </div>
            </div>
            <button
              onClick={handleDismissNotification}
              className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg font-semibold transition-all flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Check</span>
            </button>
          </div>
        </div>
      )}

      <nav className="navbar px-6 py-4 border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/logo-light.png" alt="Dexreaction" className="h-10" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Admin Panel
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-300">
              {adminUsername}
            </div>
            <button 
              onClick={() => setShowExportModal(true)} 
              className="btn-primary flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Export Report</span>
            </button>
            <button onClick={() => setShowCredentialsModal(true)} className="btn-secondary">
              Change Credentials
            </button>
            <button onClick={handleLogout} className="btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-purple-500/20 overflow-x-auto">
          {['stats', 'users', 'orders', 'vouchers', 'support', 'deposits', 'settings'].map((tab) => {
            // Check if there are pending items that need attention
            const hasPendingOrders = tab === 'orders' && !hasViewedOrders && orders.some(o => 
              o.status === 'pending_verification' || (o.status === 'pending' && o.payment_method === 'balance')
            );
            const hasPendingDeposits = tab === 'deposits' && !hasViewedDeposits && deposits.some(d => d.status === 'verifying');
            const showNewBadge = hasPendingOrders || hasPendingDeposits;
            
            return (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab === 'orders') setHasViewedOrders(true);
                  if (tab === 'deposits') setHasViewedDeposits(true);
                }}
                className={`px-6 py-3 font-semibold transition-all whitespace-nowrap relative ${
                  activeTab === tab
                    ? 'text-purple-400 border-b-2 border-purple-400'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`admin-tab-${tab}`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {showNewBadge && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-bold text-yellow-400 bg-yellow-500/20 border border-yellow-500/50 rounded animate-pulse">
                    !NEW
                  </span>
                )}
                {tab === 'deposits' && newDepositNotification && (
                  <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
                    !
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="text-white text-xl">Loading...</div>
          </div>
        )}

        {error && (
          <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="stat-card" data-testid="stat-total-users">
                <div className="text-4xl mb-2">👥</div>
                <h3 className="text-gray-400 text-sm mb-1">Total Users</h3>
                <div className="text-4xl font-bold text-purple-400">{stats.total_users}</div>
              </div>
              <div className="stat-card" data-testid="stat-total-orders">
                <div className="text-4xl mb-2">📊</div>
                <h3 className="text-gray-400 text-sm mb-1">Total Orders</h3>
                <div className="text-4xl font-bold text-purple-400">{stats.total_orders}</div>
              </div>
              <div className="stat-card" data-testid="stat-pending-orders">
                <div className="text-4xl mb-2">⏳</div>
                <h3 className="text-gray-400 text-sm mb-1">Pending Orders</h3>
                <div className="text-4xl font-bold text-yellow-400">{stats.pending_orders}</div>
              </div>
              <div className="stat-card" data-testid="stat-total-revenue">
                <div className="text-4xl mb-2">💰</div>
                <h3 className="text-gray-400 text-sm mb-1">Total Revenue</h3>
                <div className="text-4xl font-bold text-green-400">${stats.total_revenue_usd.toFixed(2)}</div>
              </div>
            </div>

          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="admin-card overflow-x-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">All Users</h2>
              <div className="flex items-center space-x-6">
                <div className="text-sm text-gray-400">
                  Total: {users.length} users
                </div>
                <div className="text-sm font-semibold">
                  <span className="text-gray-400">Total Balance:</span>{' '}
                  <span className="text-green-400">
                    ${users.reduce((sum, user) => sum + (user.balance || 0), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Balance</th>
                  <th>Support PIN</th>
                  <th>Status</th>
                  <th>2FA</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users
                  .slice((usersCurrentPage - 1) * usersPerPage, usersCurrentPage * usersPerPage)
                  .map((user) => (
                    <tr key={user.id} className={user.is_blocked ? 'opacity-60' : ''}>
                      <td>{user.email}</td>
                      <td>{user.full_name}</td>
                      <td>
                        <span className="font-semibold text-green-400">
                          ${(user.balance || 0).toFixed(2)}
                        </span>
                      </td>
                      <td><code className="text-purple-400">{user.support_pin}</code></td>
                      <td>
                        {user.is_blocked ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-400 border border-red-500/30">
                            Blocked
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                            Active
                          </span>
                        )}
                      </td>
                      <td>{user.two_fa_enabled ? '✅' : '❌'}</td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => handleViewUserDetail(user)}
                          className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded hover:bg-purple-500/30 transition-colors text-sm"
                        >
                          View Detail
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {/* Users Pagination */}
            {users.length > usersPerPage && (
              <div className="mt-6 flex justify-center items-center space-x-2">
                <button
                  onClick={() => setUsersCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={usersCurrentPage === 1}
                  className="px-4 py-2 rounded-lg bg-slate-700 border border-purple-500/20 hover:border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {Array.from({ length: Math.ceil(users.length / usersPerPage) }, (_, i) => i + 1).map((page) => {
                  const totalPages = Math.ceil(users.length / usersPerPage);
                  if (
                    page === 1 || 
                    page === totalPages || 
                    (page >= usersCurrentPage - 1 && page <= usersCurrentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setUsersCurrentPage(page)}
                        className={`px-4 py-2 rounded-lg border transition-all ${
                          usersCurrentPage === page
                            ? 'bg-purple-500 border-purple-500 text-white font-bold'
                            : 'bg-slate-700 border-purple-500/20 hover:border-purple-500/50 text-gray-300'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === usersCurrentPage - 2 || page === usersCurrentPage + 2) {
                    return <span key={page} className="text-gray-500 px-2">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() => setUsersCurrentPage(prev => Math.min(prev + 1, Math.ceil(users.length / usersPerPage)))}
                  disabled={usersCurrentPage === Math.ceil(users.length / usersPerPage)}
                  className="px-4 py-2 rounded-lg bg-slate-700 border border-purple-500/20 hover:border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <div className="ml-4 text-sm text-gray-400">
                  Page {usersCurrentPage} of {Math.ceil(users.length / usersPerPage)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="admin-card">
            <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
              <h2 className="text-2xl font-bold">All Orders</h2>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      const response = await adminAPI.syncNowpaymentsStatus();
                      if (response.data.synced_count > 0) {
                        alert(`Synced ${response.data.synced_count} orders`);
                        fetchOrders();
                      } else {
                        alert('All orders are already in sync');
                      }
                    } catch (err) {
                      alert('Failed to sync: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sync Status
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Expire all pending orders older than 30 minutes?')) return;
                    try {
                      const response = await adminAPI.expirePendingOrders();
                      if (response.data.expired_count > 0) {
                        alert(`Expired ${response.data.expired_count} orders`);
                        fetchOrders();
                      } else {
                        alert('No orders to expire');
                      }
                    } catch (err) {
                      alert('Failed: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Expire Old
                </button>
              </div>
            </div>
            
            {/* Search and Filter Controls */}
            <div className="mb-6 space-y-4">
              {/* Search Bar */}
              <div>
                <input
                  type="text"
                  placeholder="Search by Order ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field w-full md:w-96"
                />
              </div>
              
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="input-field"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="pending_verification">Pending Verification</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">User</label>
                  <select
                    value={filterUser}
                    onChange={(e) => setFilterUser(e.target.value)}
                    className="input-field"
                  >
                    <option value="all">All Users</option>
                    {[...new Set(orders.map(o => o.user_email))].map(email => (
                      <option key={email} value={email}>{email}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Date</label>
                  <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="input-field"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilterStatus('all');
                      setFilterUser('all');
                      setFilterDate('');
                    }}
                    className="btn-secondary w-full"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Reaction</th>
                    <th>Quantity</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>DexScreener</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filteredOrders = orders.filter(order => {
                      // Search filter
                      if (searchQuery && !order.id.toLowerCase().includes(searchQuery.toLowerCase())) {
                        return false;
                      }
                      
                      // Status filter
                      if (filterStatus !== 'all' && order.status !== filterStatus) {
                        return false;
                      }
                      
                      // User filter
                      if (filterUser !== 'all' && order.user_email !== filterUser) {
                        return false;
                      }
                      
                      // Date filter
                      if (filterDate) {
                        const orderDate = new Date(order.created_at).toISOString().split('T')[0];
                        if (orderDate !== filterDate) {
                          return false;
                        }
                      }
                      
                      return true;
                    });

                    const startIndex = (ordersCurrentPage - 1) * ordersPerPage;
                    const endIndex = startIndex + ordersPerPage;
                    const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

                    return paginatedOrders.map((order) => (
                  <tr key={order.id} data-testid={`admin-order-${order.id}`}>
                    <td className="text-sm" title={order.user_email}>{formatEmail(order.user_email)}</td>
                    <td className="capitalize">{order.reaction_type}</td>
                    <td>{order.quantity}</td>
                    <td>
                      <div>${(order.total_usd || 0).toFixed(2)}</div>
                      {order.total_sol ? (
                        <div className="text-sm text-gray-400">{order.total_sol.toFixed(4)} SOL</div>
                      ) : order.payment_method === 'nowpayments' ? (
                        <div className="text-xs text-blue-400">via NOWPayments</div>
                      ) : null}
                      {order.payment_method === 'manual' && order.unique_identifier && (
                        <div className="text-xs text-yellow-400">ID: {order.unique_identifier}</div>
                      )}
                    </td>
                    <td className="capitalize">{order.payment_method || 'N/A'}</td>
                    <td>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        order.source === 'telegram' 
                          ? 'bg-blue-500/20 text-blue-400' 
                          : order.source === 'guest'
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'bg-green-500/20 text-green-400'
                      }`} title={order.source}>
                        {formatSource(order.source)}
                      </span>
                    </td>
                    <td>
                      <div className="space-y-1">
                        <span className={`status-badge status-${order.status}`}>
                          {order.status === 'pending_verification' ? 'Pending Verification' : 
                           order.status === 'processing' ? 'Processing' :
                           order.status}
                        </span>
                        
                        {/* Vendor Badge - Only for completed orders */}
                        {(order.status === 'completed' || order.status === 'processing') && order.vendor_used && (
                          <div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                              order.vendor_used === 'dexmoji' 
                                ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30' 
                                : 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                            }`} title={`Fulfilled by ${order.vendor_used === 'dexmoji' ? 'Dexmoji' : 'Boostlegends'}`}>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              {order.vendor_used === 'dexmoji' ? 'Dexmoji' : 'Boostlegends'}
                            </span>
                          </div>
                        )}
                        
                        {/* Vendor Order ID - for admin tracking */}
                        {order.status === 'completed' && (order.boostlegends_order_id || order.dexmoji_order_id) && (
                          <div className="text-xs text-gray-500" title="Vendor Order ID">
                            ID: {order.boostlegends_order_id || order.dexmoji_order_id}
                          </div>
                        )}
                        
                        {order.transaction_signature && (
                          <a 
                            href={`https://solscan.io/tx/${order.transaction_signature}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:text-blue-300 block"
                          >
                            View TX
                          </a>
                        )}
                      </div>
                    </td>
                    <td>
                      <a href={order.dexscreener_link} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 text-sm">
                        View
                      </a>
                    </td>
                    <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    <td className="relative">
                      <button
                        onClick={() => setOpenDropdown(openDropdown === order.id ? null : order.id)}
                        className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2 transition-colors"
                      >
                        <span>Actions</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {/* Dropdown Menu */}
                      {openDropdown === order.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-purple-500/30 rounded-lg shadow-xl z-10">
                          <div className="py-1">
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowOrderDetailModal(true);
                                setOpenDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-purple-500/20 hover:text-white transition-colors flex items-center space-x-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span>View Detail</span>
                            </button>
                            
                            {order.status === 'pending' && order.payment_method === 'balance' && (
                              <button
                                onClick={() => handleProcessOrder(order.id)}
                                className="w-full text-left px-4 py-2 text-sm text-purple-400 hover:bg-purple-500/20 hover:text-purple-300 transition-colors flex items-center space-x-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                <span>Process Order (Balance)</span>
                              </button>
                            )}
                            
                            {order.status === 'pending_verification' && (
                              <>
                                <button
                                  onClick={() => handleAcceptPayment(order.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-green-400 hover:bg-green-500/20 hover:text-green-300 transition-colors flex items-center space-x-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>Accept Payment</span>
                                </button>
                                
                                <button
                                  onClick={() => handleProcessOrder(order.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-purple-400 hover:bg-purple-500/20 hover:text-purple-300 transition-colors flex items-center space-x-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  <span>Process Order</span>
                                </button>
                                
                                <button
                                  onClick={() => handleRejectPayment(order.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-colors flex items-center space-x-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  <span>Reject Payment</span>
                                </button>
                              </>
                            )}
                            
                            {order.status === 'processing' && (
                              <>
                                {order.payment_method === 'balance' && (
                                  <button
                                    onClick={() => handleProcessOrder(order.id)}
                                    className="w-full text-left px-4 py-2 text-sm text-purple-400 hover:bg-purple-500/20 hover:text-purple-300 transition-colors flex items-center space-x-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    <span>Process Order (Auto)</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleCompleteOrder(order.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-blue-400 hover:bg-blue-500/20 hover:text-blue-300 transition-colors flex items-center space-x-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span>Complete Order</span>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                    ));
                  })()}
              </tbody>
            </table>
            </div>

            {/* Orders Pagination */}
            {(() => {
              const filteredOrders = orders.filter(order => {
                if (searchQuery && !order.id.toLowerCase().includes(searchQuery.toLowerCase())) return false;
                if (filterStatus !== 'all' && order.status !== filterStatus) return false;
                if (filterUser !== 'all' && order.user_email !== filterUser) return false;
                if (filterDate) {
                  const orderDate = new Date(order.created_at).toISOString().split('T')[0];
                  if (orderDate !== filterDate) return false;
                }
                return true;
              });

              if (filteredOrders.length <= ordersPerPage) return null;

              return (
                <div className="mt-6 flex justify-center items-center space-x-2">
                  <button
                    onClick={() => setOrdersCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={ordersCurrentPage === 1}
                    className="px-4 py-2 rounded-lg bg-slate-700 border border-purple-500/20 hover:border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {Array.from({ length: Math.ceil(filteredOrders.length / ordersPerPage) }, (_, i) => i + 1).map((page) => {
                    const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
                    if (
                      page === 1 || 
                      page === totalPages || 
                      (page >= ordersCurrentPage - 1 && page <= ordersCurrentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setOrdersCurrentPage(page)}
                          className={`px-4 py-2 rounded-lg border transition-all ${
                            ordersCurrentPage === page
                              ? 'bg-purple-500 border-purple-500 text-white font-bold'
                              : 'bg-slate-700 border-purple-500/20 hover:border-purple-500/50 text-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === ordersCurrentPage - 2 || page === ordersCurrentPage + 2) {
                      return <span key={page} className="text-gray-500 px-2">...</span>;
                    }
                    return null;
                  })}

                  <button
                    onClick={() => setOrdersCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredOrders.length / ordersPerPage)))}
                    disabled={ordersCurrentPage === Math.ceil(filteredOrders.length / ordersPerPage)}
                    className="px-4 py-2 rounded-lg bg-slate-700 border border-purple-500/20 hover:border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <div className="ml-4 text-sm text-gray-400">
                    Page {ordersCurrentPage} of {Math.ceil(filteredOrders.length / ordersPerPage)} ({filteredOrders.length} orders)
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Vouchers Tab */}
        {activeTab === 'vouchers' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Voucher Management</h2>
              <button onClick={() => setShowVoucherModal(true)} className="btn-primary">
                Create Voucher
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vouchers.map((voucher) => (
                <div key={voucher.id} className="admin-card">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-purple-400 font-mono">{voucher.code}</h3>
                      <p className="text-sm text-gray-400">Created: {new Date(voucher.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`status-badge ${voucher.active ? 'status-completed' : 'status-failed'}`}>
                      {voucher.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Discount:</span>
                      <span className="text-white font-semibold">
                        {voucher.discount_type === 'percentage' ? `${voucher.discount_value}%` : `$${voucher.discount_value}`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Min Purchase:</span>
                      <span className="text-white">${voucher.min_purchase}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Used:</span>
                      <span className="text-white">{voucher.used_count} {voucher.max_uses ? `/ ${voucher.max_uses}` : ''}</span>
                    </div>
                    {voucher.expires_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Expires:</span>
                        <span className="text-white text-sm">{new Date(voucher.expires_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleToggleVoucher(voucher.id, voucher.active)}
                      className={`flex-1 px-3 py-2 rounded-lg font-semibold ${voucher.active ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'}`}
                    >
                      {voucher.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDeleteVoucher(voucher.id)}
                      className="px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <div className="grid grid-cols-12 gap-6">
            {/* Ticket List */}
            <div className="col-span-4">
              <div className="reaction-card">
                <h2 className="text-2xl font-bold mb-4">Support Tickets</h2>
                
                {/* Filter Buttons */}
                <div className="flex space-x-2 mb-4">
                  {['all', 'open', 'in-progress', 'resolved', 'closed'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setTicketFilter(filter)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                        ticketFilter === filter
                          ? 'bg-purple-500 text-white'
                          : 'bg-slate-800 text-gray-400 hover:text-white'
                      }`}
                    >
                      {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                </div>
                
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {tickets
                    .filter((ticket) => ticketFilter === 'all' || ticket.status === ticketFilter)
                    .map((ticket) => {
                      const statusColors = {
                        'open': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                        'replied': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                        'in-progress': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
                        'resolved': 'bg-green-500/20 text-green-400 border-green-500/30',
                        'closed': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                      };
                      
                      return (
                        <div
                          key={ticket.id}
                          onClick={() => setActiveTicket(ticket)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            activeTicket?.id === ticket.id
                              ? 'border-purple-500 bg-purple-500/20'
                              : 'border-purple-500/20 hover:border-purple-500/50'
                          }`}
                        >
                          <div className="font-semibold mb-1">{ticket.subject}</div>
                          <div className="text-sm text-gray-400 mb-2">{ticket.user_email}</div>
                          {ticket.order_id && (
                            <div className="text-xs text-gray-500 mb-2">Order: {ticket.order_id.slice(0, 8)}...</div>
                          )}
                          <div className="flex justify-between items-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${statusColors[ticket.status] || statusColors['open']}`}>
                              {ticket.status}
                            </span>
                            <span className="text-xs text-gray-400">{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div className="col-span-8">
              {activeTicket ? (
                <div className="reaction-card flex flex-col" style={{ height: '70vh' }}>
                  <div className="border-b border-purple-500/20 pb-4 mb-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h2 className="text-xl font-bold mb-1">{activeTicket.subject}</h2>
                        <p className="text-gray-400 text-sm">{activeTicket.user_email} - Ticket #{activeTicket.id.slice(0, 8)}</p>
                        {activeTicket.order_id && (
                          <p className="text-gray-500 text-xs mt-1">Related Order: {activeTicket.order_id.slice(0, 8)}...</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        {/* Status Selector */}
                        <select
                          value={activeTicket.status}
                          onChange={(e) => handleUpdateTicketStatus(activeTicket.id, e.target.value)}
                          className="px-3 py-2 bg-slate-800 border border-purple-500/30 rounded-lg text-sm font-semibold"
                        >
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Initial Message */}
                    <div className="p-3 bg-slate-800/50 rounded-lg text-sm">
                      <p className="text-gray-400 mb-1 font-semibold">Initial Message:</p>
                      <p className="text-gray-300">{activeTicket.message}</p>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs md:max-w-md px-4 py-3 rounded-lg ${
                            msg.sender_type === 'admin'
                              ? 'bg-purple-500 text-white'
                              : 'bg-gray-700 text-gray-100'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-semibold ${msg.sender_type === 'admin' ? 'text-purple-200' : 'text-gray-400'}`}>
                              {msg.sender_type === 'admin' ? '🛠️ Admin' : '👤 User'}
                            </span>
                          </div>
                          <div className="text-sm mb-2">{msg.message}</div>
                          <div className={`text-xs ${msg.sender_type === 'admin' ? 'text-purple-200' : 'text-gray-400'}`}>
                            {new Date(msg.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(msg.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  {activeTicket.status === 'closed' ? (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                      <p className="text-red-400 font-semibold">This ticket is closed</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSendMessage} className="flex space-x-3">
                      <input
                        type="text"
                        placeholder="Type your reply..."
                        className="input-field flex-1"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                      />
                      <button type="submit" className="btn-primary px-6">
                        Send
                      </button>
                    </form>
                  )}
                </div>
              ) : (
                <div className="reaction-card text-center py-12">
                  <p className="text-gray-400 text-lg">Select a ticket to view conversation</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Credentials Modal */}
      {showCredentialsModal && (
        <div className="modal" onClick={() => setShowCredentialsModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Change Admin Credentials</h2>
            <form onSubmit={handleUpdateCredentials} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">New Username</label>
                <input
                  type="text"
                  className="input-field"
                  value={newCredentials.new_username}
                  onChange={(e) => setNewCredentials({ ...newCredentials, new_username: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">New Password</label>
                <input
                  type="password"
                  className="input-field"
                  value={newCredentials.new_password}
                  onChange={(e) => setNewCredentials({ ...newCredentials, new_password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div className="flex space-x-3">
                <button type="submit" className="btn-primary flex-1">
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setShowCredentialsModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Voucher Modal */}
      {showVoucherModal && (
        <div className="modal" onClick={() => setShowVoucherModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Create New Voucher</h2>
            <form onSubmit={handleCreateVoucher} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Voucher Code</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="WELCOME10"
                  value={voucherForm.code}
                  onChange={(e) => setVoucherForm({ ...voucherForm, code: e.target.value.toUpperCase() })}
                  required
                  minLength={4}
                  maxLength={20}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Discount Type</label>
                <select
                  className="input-field"
                  value={voucherForm.discount_type}
                  onChange={(e) => setVoucherForm({ ...voucherForm, discount_type: e.target.value })}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Discount Value</label>
                <input
                  type="number"
                  className="input-field"
                  placeholder={voucherForm.discount_type === 'percentage' ? '10' : '5'}
                  value={voucherForm.discount_value}
                  onChange={(e) => setVoucherForm({ ...voucherForm, discount_value: e.target.value })}
                  required
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Minimum Purchase ($)</label>
                <input
                  type="number"
                  className="input-field"
                  placeholder="0"
                  value={voucherForm.min_purchase}
                  onChange={(e) => setVoucherForm({ ...voucherForm, min_purchase: e.target.value })}
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Uses (optional)</label>
                <input
                  type="number"
                  className="input-field"
                  placeholder="Unlimited"
                  value={voucherForm.max_uses}
                  onChange={(e) => setVoucherForm({ ...voucherForm, max_uses: e.target.value })}
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Expires At (optional)</label>
                <input
                  type="date"
                  className="input-field"
                  value={voucherForm.expires_at}
                  onChange={(e) => setVoucherForm({ ...voucherForm, expires_at: e.target.value })}
                />
              </div>
              <div className="flex space-x-3">
                <button type="submit" className="btn-primary flex-1">
                  Create Voucher
                </button>
                <button
                  type="button"
                  onClick={() => setShowVoucherModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete All Data Modal removed */}



      {/* Order Detail Modal */}
      {showOrderDetailModal && selectedOrder && (
        <div className="modal" onClick={() => setShowOrderDetailModal(false)}>
          <div className="modal-content max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6 sticky top-0 bg-slate-900 z-10 pb-4">
              <div>
                <h2 className="text-2xl font-bold mb-2">Order Details</h2>
                <p className="text-gray-400 text-sm">Order ID: {selectedOrder.id}</p>
              </div>
              <button 
                onClick={() => setShowOrderDetailModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Status & User Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-2">Status</p>
                <span className={`status-badge status-${selectedOrder.status} text-lg`}>
                  {selectedOrder.status === 'pending_verification' ? 'Pending Verification' : 
                   selectedOrder.status === 'processing' ? 'Processing' :
                   selectedOrder.status}
                </span>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-2">Customer</p>
                <p className="font-semibold">{selectedOrder.user_email}</p>
              </div>
            </div>

            {/* Order Details */}
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold mb-4 text-lg">Order Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Reaction Type</p>
                  <p className="font-semibold capitalize">{selectedOrder.reaction_type}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Quantity</p>
                  <p className="font-semibold">{selectedOrder.quantity} reactions</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total (USD)</p>
                  <p className="font-semibold text-purple-400">${(selectedOrder.total_usd || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total (SOL)</p>
                  <p className="font-semibold text-green-400">
                    {selectedOrder.total_sol ? `${selectedOrder.total_sol.toFixed(6)} SOL` : 'N/A (Crypto Payment)'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Payment Method</p>
                  <p className="font-semibold capitalize">{selectedOrder.payment_method || 'N/A'}</p>
                </div>
                {selectedOrder.unique_identifier && (
                  <div>
                    <p className="text-gray-400 text-sm">Unique ID</p>
                    <p className="font-semibold text-yellow-400">{selectedOrder.unique_identifier}</p>
                  </div>
                )}
                {selectedOrder.source === 'guest' && (
                  <div>
                    <p className="text-gray-400 text-sm">Order Source</p>
                    <p className="font-semibold text-purple-400">Guest Order (No Account)</p>
                  </div>
                )}
              </div>
            </div>

            {/* NOWPayments Payment Details - Real-time Status */}
            {selectedOrder.payment_method === 'nowpayments' && (
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg text-blue-400 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    NOWPayments Details
                    {loadingNowpaymentsStatus && (
                      <span className="ml-2 animate-spin">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </span>
                    )}
                  </h3>
                  <span className="text-xs text-gray-400">Auto-refresh: 15s</span>
                </div>
                
                {nowpaymentsError ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                    {nowpaymentsError}
                  </div>
                ) : nowpaymentsStatus ? (
                  <div className="space-y-4">
                    {/* Payment Status Badge */}
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-400 text-sm">Payment Status:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        nowpaymentsStatus.payment_status === 'finished' || nowpaymentsStatus.payment_status === 'confirmed'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : nowpaymentsStatus.payment_status === 'waiting'
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          : nowpaymentsStatus.payment_status === 'confirming' || nowpaymentsStatus.payment_status === 'sending'
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : nowpaymentsStatus.payment_status === 'expired' || nowpaymentsStatus.payment_status === 'failed'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                      }`}>
                        {nowpaymentsStatus.payment_status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                      {nowpaymentsStatus.cached && (
                        <span className="text-xs text-orange-400">(cached)</span>
                      )}
                    </div>
                    
                    {/* Payment Address with QR */}
                    {nowpaymentsStatus.pay_address && (
                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-gray-400 text-sm mb-2">Payment Address ({nowpaymentsStatus.pay_currency?.toUpperCase()})</p>
                        <div className="flex items-start space-x-4">
                          <div className="flex-1">
                            <p className="font-mono text-sm break-all text-white bg-slate-900/50 p-2 rounded">
                              {nowpaymentsStatus.pay_address}
                            </p>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(nowpaymentsStatus.pay_address);
                                alert('Address copied!');
                              }}
                              className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                            >
                              📋 Copy Address
                            </button>
                          </div>
                          {/* QR Code from NOWPayments */}
                          {nowpaymentsStatus.payment_id && (
                            <div className="flex-shrink-0">
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/payments/nowpayments/${nowpaymentsStatus.payment_id}/qr`}
                                alt="Payment QR Code"
                                className="w-24 h-24 rounded bg-white p-1"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Payment Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Payment ID</p>
                        <p className="font-mono text-sm text-white">{nowpaymentsStatus.payment_id || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Expected Amount</p>
                        <p className="font-semibold text-cyan-400">
                          {nowpaymentsStatus.pay_amount} {nowpaymentsStatus.pay_currency?.toUpperCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Price Amount</p>
                        <p className="font-semibold text-green-400">
                          ${nowpaymentsStatus.price_amount?.toFixed(2)} {nowpaymentsStatus.price_currency?.toUpperCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Actually Paid</p>
                        <p className={`font-semibold ${
                          nowpaymentsStatus.actually_paid > 0 ? 'text-green-400' : 'text-gray-400'
                        }`}>
                          {nowpaymentsStatus.actually_paid || 0} {nowpaymentsStatus.pay_currency?.toUpperCase()}
                        </p>
                      </div>
                      {nowpaymentsStatus.expiration_estimate_date && (
                        <div className="col-span-2">
                          <p className="text-gray-400 text-xs mb-1">Expires At</p>
                          <p className="font-semibold text-orange-400">
                            {new Date(nowpaymentsStatus.expiration_estimate_date).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Last Checked */}
                    {nowpaymentsStatus.last_checked_at && (
                      <div className="pt-3 border-t border-blue-500/20">
                        <p className="text-xs text-gray-500">
                          Last updated: {new Date(nowpaymentsStatus.last_checked_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p className="text-gray-400 text-sm">Loading payment details...</p>
                  </div>
                )}
              </div>
            )}

            {/* Partial Payment Warning & Manual Process Button */}
            {selectedOrder.payment_method === 'nowpayments' && 
             (selectedOrder.is_partially_paid || nowpaymentsStatus?.payment_status === 'partially_paid') && 
             selectedOrder.status === 'pending' && (
              <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/50 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">⚠️</span>
                  <div className="flex-1">
                    <h3 className="font-bold text-orange-400 text-lg mb-2">Pembayaran Partial</h3>
                    <p className="text-sm text-gray-300 mb-3">
                      Customer mengirim pembayaran kurang dari yang diperlukan.
                    </p>
                    <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 text-sm mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Expected:</span>
                        <span className="text-white font-mono">
                          {nowpaymentsStatus?.pay_amount || selectedOrder.nowpayments?.pay_amount} {(nowpaymentsStatus?.pay_currency || selectedOrder.nowpayments?.pay_currency)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Received:</span>
                        <span className="text-orange-400 font-mono">
                          {nowpaymentsStatus?.actually_paid || selectedOrder.nowpayments?.actually_paid || selectedOrder.partial_amount_received || 0} {(nowpaymentsStatus?.pay_currency || selectedOrder.nowpayments?.pay_currency)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-slate-700 pt-2">
                        <span className="text-gray-400">Shortage:</span>
                        <span className="text-red-400 font-mono">
                          {((nowpaymentsStatus?.pay_amount || selectedOrder.nowpayments?.pay_amount || 0) - 
                            (nowpaymentsStatus?.actually_paid || selectedOrder.nowpayments?.actually_paid || selectedOrder.partial_amount_received || 0)).toFixed(8)} {(nowpaymentsStatus?.pay_currency || selectedOrder.nowpayments?.pay_currency)?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          handleProcessOrder(selectedOrder.id);
                          setShowOrderDetailModal(false);
                        }}
                        className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Process Manual
                      </button>
                      <button
                        onClick={() => setShowOrderDetailModal(false)}
                        className="py-2 px-4 bg-slate-600 hover:bg-slate-700 rounded-lg font-semibold text-white transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Klik "Process Manual" untuk memproses pesanan ini meskipun pembayaran kurang.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Fulfillment Information - Admin Only */}
            {(selectedOrder.status === 'completed' || selectedOrder.status === 'processing') && selectedOrder.vendor_used && (
              <div className={`rounded-lg p-4 mb-6 border ${
                selectedOrder.vendor_used === 'dexmoji'
                  ? 'bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border-teal-500/30'
                  : 'bg-gradient-to-br from-purple-500/10 to-blue-500/10 border-purple-500/30'
              }`}>
                <h3 className={`font-semibold mb-4 text-lg flex items-center ${
                  selectedOrder.vendor_used === 'dexmoji' ? 'text-teal-400' : 'text-purple-400'
                }`}>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Fulfillment Information
                  <span className="ml-2 text-xs px-2 py-0.5 bg-slate-900/50 rounded-full text-gray-400">Admin Only</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Fulfilled By</p>
                    <p className={`font-bold text-lg ${
                      selectedOrder.vendor_used === 'dexmoji' ? 'text-teal-300' : 'text-purple-300'
                    }`}>
                      {selectedOrder.vendor_used === 'dexmoji' ? 'Dexmoji' : 'Boostlegends'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Vendor Order ID</p>
                    <p className="font-semibold text-white font-mono text-sm">
                      {selectedOrder.boostlegends_order_id || selectedOrder.dexmoji_order_id || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Fulfillment Method</p>
                    <p className="font-semibold text-white capitalize">
                      {selectedOrder.fulfillment_method || 'manual'}
                    </p>
                  </div>
                </div>
                {selectedOrder.completed_at && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-gray-400 text-xs">
                      Completed at: {new Date(selectedOrder.completed_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Voucher Discount Information */}
            {selectedOrder.voucher_code && selectedOrder.discount_amount > 0 && (
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
                <h3 className="font-semibold mb-4 text-lg text-green-400 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Voucher Discount Applied
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Voucher Code</p>
                    <p className="font-bold text-green-400 text-lg">{selectedOrder.voucher_code}</p>
                  </div>
                  {selectedOrder.original_total_usd && (
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Original Price</p>
                      <p className="font-semibold text-gray-300">${(selectedOrder.original_total_usd || 0).toFixed(2)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Discount Amount</p>
                    <p className="font-semibold text-green-400">-${(selectedOrder.discount_amount || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Final Price (After Discount)</p>
                    <p className="font-bold text-green-400">${(selectedOrder.total_usd || 0).toFixed(2)}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-green-500/20">
                  <p className="text-xs text-gray-400">
                    Customer saved <span className="text-green-400 font-semibold">${(selectedOrder.discount_amount || 0).toFixed(2)}</span> with this voucher
                  </p>
                </div>
              </div>
            )}

            {/* DexScreener Link */}
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-gray-400 text-sm mb-2">DexScreener Link</p>
              <a 
                href={selectedOrder.dexscreener_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 break-all"
              >
                {selectedOrder.dexscreener_link}
              </a>
            </div>

            {/* Transaction Details */}
            {selectedOrder.transaction_signature && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <h3 className="font-semibold mb-3 text-blue-400">Transaction Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Transaction Signature</p>
                    <p className="font-mono text-xs break-all text-white mb-2">{selectedOrder.transaction_signature}</p>
                    <a
                      href={`https://solscan.io/tx/${selectedOrder.transaction_signature}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm inline-block transition-colors"
                    >
                      View on Solscan →
                    </a>
                  </div>
                  {selectedOrder.submitted_at && (
                    <div>
                      <p className="text-gray-400 text-sm">Submitted At</p>
                      <p className="font-semibold">{new Date(selectedOrder.submitted_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Boostlegends Order Information */}
            {selectedOrder.boostlegends_order_id && (
              <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
                <h3 className="font-semibold mb-3 text-purple-400 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Boostlegends Fulfillment
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Boostlegends Order ID</p>
                    <p className="font-mono text-lg font-bold text-purple-400">{selectedOrder.boostlegends_order_id}</p>
                  </div>
                  {selectedOrder.fulfillment_method && (
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Fulfillment Method</p>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                        selectedOrder.fulfillment_method === 'auto' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                      }`}>
                        {selectedOrder.fulfillment_method === 'auto' ? '🚀 Auto' : '👤 Manual'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-purple-500/20">
                  <p className="text-xs text-gray-400">
                    This order was fulfilled via Boostlegends API. You can track the order status on their platform.
                  </p>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm">Created At</p>
                <p className="font-semibold">{new Date(selectedOrder.created_at).toLocaleString()}</p>
              </div>
              {selectedOrder.verified_at && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Verified At</p>
                  <p className="font-semibold">{new Date(selectedOrder.verified_at).toLocaleString()}</p>
                </div>
              )}
              {selectedOrder.completed_at && (
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Completed At</p>
                  <p className="font-semibold">{new Date(selectedOrder.completed_at).toLocaleString()}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="border-t border-purple-500/20 pt-6">
              <div className="flex space-x-4">
                {selectedOrder.status === 'pending_verification' && (
                  <>
                    <button
                      onClick={async () => {
                        await handleAcceptPayment(selectedOrder.id);
                        setShowOrderDetailModal(false);
                      }}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                      ✓ Accept Payment → Processing
                    </button>
                  </>
                )}
                {selectedOrder.status === 'processing' && (
                  <button
                    onClick={async () => {
                      await handleCompleteOrder(selectedOrder.id);
                      setShowOrderDetailModal(false);
                    }}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    ✓ Mark as Completed
                  </button>
                )}
                {selectedOrder.status === 'completed' && (
                  <div className="flex-1 bg-green-500/20 border border-green-500/30 text-green-400 px-6 py-3 rounded-lg font-semibold text-center">
                    ✓ Order Completed
                  </div>
                )}
                <button
                  onClick={() => setShowOrderDetailModal(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Deposits Tab */}
        {activeTab === 'deposits' && (
          <div className="space-y-6 max-w-7xl mx-auto">
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by user email or transaction signature..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-purple-500/30 rounded-xl text-white focus:outline-none focus:border-purple-400"
                />
              </div>
              <select
                value={depositFilter}
                onChange={(e) => setDepositFilter(e.target.value)}
                className="px-4 py-3 bg-gray-800/50 border border-purple-500/30 rounded-xl text-white focus:outline-none focus:border-purple-400"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="verifying">Verifying</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Deposits Table */}
            <div className="reaction-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-purple-500/20">
                      <th className="text-left px-6 py-4 text-gray-400 font-semibold">User Email</th>
                      <th className="text-left px-6 py-4 text-gray-400 font-semibold">Amount</th>
                      <th className="text-left px-6 py-4 text-gray-400 font-semibold">Transaction Signature</th>
                      <th className="text-left px-6 py-4 text-gray-400 font-semibold">Status</th>
                      <th className="text-left px-6 py-4 text-gray-400 font-semibold">Date</th>
                      <th className="text-left px-6 py-4 text-gray-400 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Filter deposits
                      let filtered = deposits.filter(deposit => {
                        const matchesSearch = 
                          deposit.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          deposit.transaction_signature?.toLowerCase().includes(searchQuery.toLowerCase());
                        const matchesStatus = depositFilter === 'all' || deposit.status === depositFilter;
                        return matchesSearch && matchesStatus;
                      });

                      // Paginate
                      const indexOfLastDeposit = depositsCurrentPage * depositsPerPage;
                      const indexOfFirstDeposit = indexOfLastDeposit - depositsPerPage;
                      const currentDeposits = filtered.slice(indexOfFirstDeposit, indexOfLastDeposit);
                      const totalPages = Math.ceil(filtered.length / depositsPerPage);

                      if (currentDeposits.length === 0) {
                        return (
                          <tr>
                            <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                              No deposits found
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <>
                          {currentDeposits.map((deposit) => (
                            <tr 
                              key={deposit.id} 
                              className="border-b border-purple-500/10 hover:bg-purple-500/5 transition-colors cursor-pointer"
                              onClick={() => {
                                setSelectedDeposit(deposit);
                                setShowDepositDetailModal(true);
                              }}
                            >
                              <td className="px-6 py-4 text-white">{deposit.user_email || 'Unknown'}</td>
                              <td className="px-6 py-4 text-white font-semibold">${deposit.amount.toFixed(2)}</td>
                              <td className="px-6 py-4">
                                {deposit.transaction_signature ? (
                                  <a
                                    href={`https://solscan.io/tx/${deposit.transaction_signature}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-purple-400 hover:text-purple-300 text-sm font-mono underline"
                                  >
                                    {deposit.transaction_signature.substring(0, 20)}...
                                  </a>
                                ) : (
                                  <span className="text-gray-400 text-sm font-mono">N/A</span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  deposit.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                  deposit.status === 'verifying' ? 'bg-yellow-500/20 text-yellow-400' :
                                  deposit.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {deposit.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-gray-400 text-sm">
                                {new Date(deposit.created_at).toLocaleString()}
                              </td>
                              <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                {deposit.status === 'verifying' && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleApproveDeposit(deposit.id)}
                                      className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-semibold"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleRejectDeposit(deposit.id)}
                                      className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-semibold"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                )}
                                {deposit.status !== 'verifying' && (
                                  <span className="text-gray-500 text-sm">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {totalPages > 1 && (
                            <tr>
                              <td colSpan="6" className="px-6 py-4">
                                <div className="flex justify-center items-center gap-2">
                                  <button
                                    onClick={() => setDepositsCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={depositsCurrentPage === 1}
                                    className="px-4 py-2 bg-gray-800/50 text-white rounded-lg hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Previous
                                  </button>
                                  <span className="text-gray-400">
                                    Page {depositsCurrentPage} of {totalPages}
                                  </span>
                                  <button
                                    onClick={() => setDepositsCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={depositsCurrentPage === totalPages}
                                    className="px-4 py-2 bg-gray-800/50 text-white rounded-lg hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Next
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="reaction-card text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {deposits.filter(d => d.status === 'verifying').length}
                </div>
                <div className="text-gray-400 text-sm mt-1">Pending Verification</div>
              </div>
              <div className="reaction-card text-center">
                <div className="text-2xl font-bold text-green-400">
                  {deposits.filter(d => d.status === 'completed').length}
                </div>
                <div className="text-gray-400 text-sm mt-1">Approved</div>
              </div>
              <div className="reaction-card text-center">
                <div className="text-2xl font-bold text-red-400">
                  {deposits.filter(d => d.status === 'rejected').length}
                </div>
                <div className="text-gray-400 text-sm mt-1">Rejected</div>
              </div>
              <div className="reaction-card text-center">
                <div className="text-2xl font-bold text-blue-400">
                  ${deposits.filter(d => d.status === 'completed').reduce((sum, d) => sum + d.amount, 0).toFixed(2)}
                </div>
                <div className="text-gray-400 text-sm mt-1">Total Approved</div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab - Boostlegends Integration */}
        {activeTab === 'settings' && (
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="reaction-card">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Boostlegends Integration</h2>
                  <p className="text-gray-400 text-sm mt-1">Configure auto-fulfillment via Boostlegends API</p>
                </div>
              </div>

              <form onSubmit={handleSaveBoostlegendsSettings} className="space-y-6">
                {/* API Key */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Boostlegends API Key *
                  </label>
                  <input
                    type="text"
                    value={boostlegendsSettings.api_key}
                    onChange={(e) => setBoostlegendsSettings({ ...boostlegendsSettings, api_key: e.target.value })}
                    className="input-field"
                    placeholder="Enter your Boostlegends API key"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Get your API key from{' '}
                    <a href="https://boostlegends.com/account" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">
                      Boostlegends Account Page
                    </a>
                  </p>
                </div>

                {/* Enable Integration */}
                <div className="flex items-center space-x-3 p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={boostlegendsSettings.enabled}
                    onChange={(e) => setBoostlegendsSettings({ ...boostlegendsSettings, enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                  />
                  <label htmlFor="enabled" className="text-gray-300 font-medium cursor-pointer">
                    Enable Boostlegends Integration
                  </label>
                </div>

                {/* Auto Fulfill */}
                <div className="flex items-center space-x-3 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <input
                    type="checkbox"
                    id="auto_fulfill"
                    checked={boostlegendsSettings.auto_fulfill}
                    onChange={(e) => setBoostlegendsSettings({ ...boostlegendsSettings, auto_fulfill: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                    disabled={!boostlegendsSettings.enabled}
                  />
                  <div>
                    <label htmlFor="auto_fulfill" className="text-gray-300 font-medium cursor-pointer block">
                      Auto-Fulfill Orders
                    </label>
                    <p className="text-xs text-gray-400 mt-1">
                      Automatically send completed orders to Boostlegends for fulfillment
                    </p>
                  </div>
                </div>

                {/* API Documentation */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-white mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    API Information
                  </h3>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• API URL: <code className="text-purple-400">https://boostlegends.com/api/v2</code></li>
                    <li>• Method: POST</li>
                    <li>• Format: JSON</li>
                    <li>• <a href="https://boostlegends.com/api" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">View Full Documentation</a></li>
                  </ul>
                </div>

                {/* Test Connection Result */}
                {connectionResult && (
                  <div className={`p-4 rounded-lg border ${
                    connectionResult.success 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    <div className="flex items-start">
                      {connectionResult.success ? (
                        <svg className="w-6 h-6 text-green-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      <div>
                        <p className={`font-semibold ${connectionResult.success ? 'text-green-400' : 'text-red-400'}`}>
                          {connectionResult.message}
                        </p>
                        {connectionResult.success && (
                          <p className="text-sm text-gray-300 mt-1">
                            Balance: <span className="font-bold text-white">{connectionResult.balance} {connectionResult.currency}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4 border-t border-purple-500/20">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        Save Settings
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={testingConnection || !boostlegendsSettings.api_key}
                    className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed flex items-center"
                  >
                    {testingConnection ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Testing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Test Connection
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Product Mappings Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Product Mappings</h2>
                  <p className="text-gray-400 text-sm mt-1">Configure service IDs for each reaction type</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-purple-500/20">
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">Reaction</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">Emoji</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">Service ID</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">Vendor</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold">Price (USD)</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productMappings.map((mapping) => (
                      <tr key={mapping.id} className="border-b border-purple-500/10 hover:bg-purple-500/5 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-semibold text-white capitalize">{mapping.name}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-2xl">{mapping.emoji}</span>
                        </td>
                        <td className="py-3 px-4">
                          {editingMapping?.id === mapping.id ? (
                            <input
                              type="number"
                              value={editingMapping.service_id || ''}
                              onChange={(e) => setEditingMapping({ ...editingMapping, service_id: parseInt(e.target.value) })}
                              className="input-field w-24"
                              placeholder="56"
                            />
                          ) : (
                            <span className="text-purple-400 font-mono">{mapping.service_id || '-'}</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {editingMapping?.id === mapping.id ? (
                            <input
                              type="text"
                              value={editingMapping.vendor || ''}
                              onChange={(e) => setEditingMapping({ ...editingMapping, vendor: e.target.value })}
                              className="input-field w-32"
                              placeholder="boostlegends"
                            />
                          ) : (
                            <span className="text-gray-300">{mapping.vendor || '-'}</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-green-400 font-semibold">${mapping.price_usd}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {editingMapping?.id === mapping.id ? (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleUpdateMapping(editingMapping)}
                                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm font-semibold transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingMapping(null)}
                                className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm font-semibold transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingMapping({ ...mapping })}
                              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <p className="text-sm text-gray-300">
                  <strong className="text-blue-400">Note:</strong> Service IDs are used for auto-fulfillment via Boostlegends API. 
                  Make sure these IDs match your Boostlegends service catalog.
                </p>
              </div>
            </div>

            {/* Google OAuth Settings Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Google OAuth Login</h2>
                  <p className="text-gray-400 text-sm mt-1">Allow users to login with their Google accounts</p>
                </div>
              </div>

              <form onSubmit={handleSaveGoogleOAuthSettings} className="space-y-6">
                {/* Client ID */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Google Client ID *
                  </label>
                  <input
                    type="text"
                    value={googleOAuthSettings.client_id}
                    onChange={(e) => setGoogleOAuthSettings({ ...googleOAuthSettings, client_id: e.target.value })}
                    className="input-field"
                    placeholder="Enter your Google OAuth Client ID"
                    required
                  />
                </div>

                {/* Client Secret */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Google Client Secret *
                  </label>
                  <input
                    type="password"
                    value={googleOAuthSettings.client_secret}
                    onChange={(e) => setGoogleOAuthSettings({ ...googleOAuthSettings, client_secret: e.target.value })}
                    className="input-field"
                    placeholder="Enter your Google OAuth Client Secret"
                    required
                  />
                </div>

                {/* Enable Google OAuth */}
                <div className="flex items-center space-x-3 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                  <input
                    type="checkbox"
                    id="google_oauth_enabled"
                    checked={googleOAuthSettings.enabled}
                    onChange={(e) => setGoogleOAuthSettings({ ...googleOAuthSettings, enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-red-500 focus:ring-red-500"
                  />
                  <label htmlFor="google_oauth_enabled" className="text-gray-300 font-medium cursor-pointer">
                    Enable Google OAuth Login
                  </label>
                </div>

                {/* Setup Instructions */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-white mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Setup Instructions
                  </h3>
                  <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
                    <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">Google Cloud Console</a></li>
                    <li>Create a new OAuth 2.0 Client ID or use existing one</li>
                    <li>Add these to <strong className="text-white">Authorized JavaScript origins</strong>:
                      <code className="block mt-1 p-2 bg-slate-900 rounded text-purple-400 text-xs">
                        {window.location.origin}
                      </code>
                    </li>
                    <li>Add these to <strong className="text-white">Authorized redirect URIs</strong>:
                      <code className="block mt-1 p-2 bg-slate-900 rounded text-purple-400 text-xs">
                        {window.location.origin}/auth/google/callback
                      </code>
                    </li>
                    <li>Copy the Client ID and Client Secret and paste them above</li>
                  </ol>
                </div>

                {/* Save Button */}
                <div className="pt-4 border-t border-purple-500/20">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 disabled:from-gray-600 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        Save Google OAuth Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Google Tag (gtag.js) Settings Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032 s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2 C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Google Tag (gtag.js)</h2>
                  <p className="text-gray-400 text-sm mt-1">Manage Google Analytics and Conversion tracking</p>
                </div>
              </div>

              <form onSubmit={handleSaveGtagSettings} className="space-y-6">
                {/* Gtag Code */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Google Tag Code *
                  </label>
                  <textarea
                    value={gtagSettings.gtag_code}
                    onChange={(e) => setGtagSettings({ ...gtagSettings, gtag_code: e.target.value })}
                    className="input-field font-mono text-xs"
                    rows={12}
                    placeholder={`<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n\n  gtag('config', 'AW-XXXXXXXXXX');\n</script>`}
                    required
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Paste your complete Google Tag code including &lt;script&gt; tags
                  </p>
                </div>

                {/* Enable Gtag */}
                <div className="flex items-center space-x-3 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <input
                    type="checkbox"
                    id="gtag_enabled"
                    checked={gtagSettings.enabled}
                    onChange={(e) => setGtagSettings({ ...gtagSettings, enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-yellow-500 focus:ring-yellow-500"
                  />
                  <label htmlFor="gtag_enabled" className="text-gray-300 font-medium cursor-pointer">
                    Enable Google Tag on all pages
                  </label>
                </div>

                {/* Info Box */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-white mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    How to Setup
                  </h3>
                  <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
                    <li>Go to <a href="https://tagmanager.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">Google Tag Manager</a> or <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">Google Analytics</a></li>
                    <li>Get your Google Tag code (starts with gtag.js or AW-XXXXXXXXXX)</li>
                    <li>Copy the complete code including &lt;script&gt; tags</li>
                    <li>Paste it in the text area above</li>
                    <li>Enable the checkbox and save</li>
                    <li>The code will be automatically injected in &lt;head&gt; section of all pages</li>
                  </ol>
                </div>

                {/* Save Button */}
                <div className="pt-4 border-t border-purple-500/20">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 disabled:from-gray-600 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        Save Google Tag Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Telegram Bot Settings Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.121.098.155.23.171.324.016.094.036.308.02.475z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Telegram Bot Integration</h2>
                  <p className="text-gray-400 text-sm mt-1">Manage Telegram Bot for transactions and notifications</p>
                </div>
              </div>

              <form onSubmit={handleSaveTelegramBotSettings} className="space-y-6">
                {/* Bot Token */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Bot Token *
                  </label>
                  <input
                    type="password"
                    value={telegramBotSettings.bot_token}
                    onChange={(e) => setTelegramBotSettings({ ...telegramBotSettings, bot_token: e.target.value })}
                    className="input-field font-mono text-sm"
                    placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Get your bot token from <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">@BotFather</a>
                  </p>
                </div>

                {/* Bot Username */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Bot Username (Optional)
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-400 mr-2">@</span>
                    <input
                      type="text"
                      value={telegramBotSettings.bot_username}
                      onChange={(e) => setTelegramBotSettings({ ...telegramBotSettings, bot_username: e.target.value })}
                      className="input-field"
                      placeholder="your_bot_username"
                    />
                  </div>
                </div>

                {/* Webhook URL */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Webhook URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={telegramBotSettings.webhook_url}
                    onChange={(e) => setTelegramBotSettings({ ...telegramBotSettings, webhook_url: e.target.value })}
                    className="input-field font-mono text-sm"
                    placeholder={`${window.location.origin}/api/telegram/webhook`}
                  />
                  <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-xs text-blue-300 font-semibold mb-1">
                      🔄 Auto Webhook Setup
                    </p>
                    <p className="text-xs text-gray-400">
                      Webhook will be automatically set via Telegram API when you save settings. Leave empty to use polling mode.
                    </p>
                  </div>
                </div>

                {/* Enable Bot */}
                <div className="flex items-center space-x-3 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <input
                    type="checkbox"
                    id="telegram_bot_enabled"
                    checked={telegramBotSettings.enabled}
                    onChange={(e) => setTelegramBotSettings({ ...telegramBotSettings, enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor="telegram_bot_enabled" className="text-gray-300 font-medium cursor-pointer">
                    Enable Telegram Bot Integration
                  </label>
                </div>

                {/* Test Connection Button */}
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={handleTestTelegramBot}
                    disabled={testingTelegramBot || !telegramBotSettings.bot_token}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center"
                  >
                    {testingTelegramBot ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Testing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Test Connection
                      </>
                    )}
                  </button>
                  
                  {telegramBotTestResult && (
                    <div className={`flex items-center px-4 py-2 rounded-lg ${telegramBotTestResult.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                      {telegramBotTestResult.success ? (
                        <>
                          <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-green-400 font-semibold text-sm">{telegramBotTestResult.message}</p>
                            {telegramBotTestResult.bot_info && (
                              <p className="text-gray-400 text-xs mt-1">
                                Bot: @{telegramBotTestResult.bot_info.username} ({telegramBotTestResult.bot_info.first_name})
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <p className="text-red-400 text-sm">{telegramBotTestResult.message}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Setup Instructions */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-white mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Setup Instructions
                  </h3>
                  <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
                    <li>Open Telegram and search for <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">@BotFather</a></li>
                    <li>Send <code className="bg-slate-900 px-2 py-1 rounded text-purple-400">/newbot</code> to create a new bot</li>
                    <li>Follow the instructions to set bot name and username</li>
                    <li>Copy the bot token and paste it above</li>
                    <li>Click "Test Connection" to verify the token</li>
                    <li>Enable the bot and save settings</li>
                    <li>
                      <strong className="text-white">For webhook:</strong> Set webhook URL to:
                      <code className="block mt-1 p-2 bg-slate-900 rounded text-purple-400 text-xs">
                        {window.location.origin}/api/telegram/webhook
                      </code>
                    </li>
                  </ol>
                </div>

                {/* Save Button */}
                <div className="pt-4 border-t border-purple-500/20">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:from-gray-600 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        Save Telegram Bot Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Fonnte WhatsApp Notification Settings Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">WhatsApp Notifications (Fonnte)</h2>
                  <p className="text-gray-400 text-sm mt-1">Get WhatsApp alerts for deposits and order verifications</p>
                </div>
              </div>

              <form onSubmit={handleSaveFonnteSettings} className="space-y-6">
                {/* Fonnte Token */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Fonnte API Token *
                  </label>
                  <input
                    type="password"
                    value={fonnteSettings.fonnte_token}
                    onChange={(e) => setFonnteSettings({ ...fonnteSettings, fonnte_token: e.target.value })}
                    className="input-field font-mono text-sm"
                    placeholder="Your Fonnte API Token"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Get your API token from <a href="https://fonnte.com" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 underline">Fonnte.com</a>
                  </p>
                </div>

                {/* Admin WhatsApp Number */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Admin WhatsApp Number *
                  </label>
                  <input
                    type="tel"
                    value={fonnteSettings.admin_whatsapp_number}
                    onChange={(e) => setFonnteSettings({ ...fonnteSettings, admin_whatsapp_number: e.target.value })}
                    className="input-field"
                    placeholder="081217460013"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Enter WhatsApp number without country code prefix
                  </p>
                </div>

                {/* Country Code */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Country Code
                  </label>
                  <input
                    type="text"
                    value={fonnteSettings.country_code}
                    onChange={(e) => setFonnteSettings({ ...fonnteSettings, country_code: e.target.value })}
                    className="input-field"
                    placeholder="62"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Default: 62 (Indonesia)
                  </p>
                </div>

                {/* Enable Fonnte */}
                <div className="flex items-center space-x-3 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <input
                    type="checkbox"
                    id="fonnte_enabled"
                    checked={fonnteSettings.enabled}
                    onChange={(e) => setFonnteSettings({ ...fonnteSettings, enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-green-500 focus:ring-green-500"
                  />
                  <label htmlFor="fonnte_enabled" className="text-gray-300 font-medium cursor-pointer">
                    Enable WhatsApp Notifications
                  </label>
                </div>

                {/* Test Connection Button */}
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={handleTestFonnteConnection}
                    disabled={testingFonnte || !fonnteSettings.fonnte_token}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center"
                  >
                    {testingFonnte ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Testing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Send Test Message
                      </>
                    )}
                  </button>
                  
                  {fonnteTestResult && (
                    <div className={`flex items-center px-4 py-2 rounded-lg ${fonnteTestResult.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                      {fonnteTestResult.success ? (
                        <>
                          <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-green-400 font-semibold text-sm">{fonnteTestResult.message}</p>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <p className="text-red-400 text-sm">{fonnteTestResult.message}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Setup Instructions */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-white mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Notification Triggers
                  </h3>
                  <ul className="text-sm text-gray-400 space-y-2 list-disc list-inside">
                    <li>🔔 When user submits deposit verification request</li>
                    <li>🔔 When user submits order payment for verification</li>
                    <li>📱 Notifications include user email, amount, and transaction details</li>
                    <li>⚡ Real-time notifications to admin WhatsApp number</li>
                  </ul>
                </div>

                {/* Save Button */}
                <div className="pt-4 border-t border-purple-500/20">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-600 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        Save WhatsApp Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* NOWPayments Settings Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">NOWPayments Settings</h2>
                  <p className="text-gray-400 text-sm mt-1">Configure crypto payment gateway for guest orders</p>
                </div>
              </div>

              <form onSubmit={handleSaveNowpaymentsSettings} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    API Key <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="password"
                    value={nowpaymentsSettings.api_key}
                    onChange={(e) => setNowpaymentsSettings({ ...nowpaymentsSettings, api_key: e.target.value })}
                    placeholder="Enter NOWPayments API Key"
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Get from: <a href="https://account.nowpayments.io/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">account.nowpayments.io/api-keys</a>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    IPN Secret Key <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="password"
                    value={nowpaymentsSettings.ipn_secret}
                    onChange={(e) => setNowpaymentsSettings({ ...nowpaymentsSettings, ipn_secret: e.target.value })}
                    placeholder="Enter IPN Secret Key"
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    For webhook signature verification
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    IPN Callback URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={nowpaymentsSettings.callback_url || `${window.location.origin}/api/webhooks/nowpayments`}
                      onChange={(e) => setNowpaymentsSettings({ ...nowpaymentsSettings, callback_url: e.target.value })}
                      placeholder={`${window.location.origin}/api/webhooks/nowpayments`}
                      className="flex-1 px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const autoUrl = `${window.location.origin}/api/webhooks/nowpayments`;
                        setNowpaymentsSettings({ ...nowpaymentsSettings, callback_url: autoUrl });
                        navigator.clipboard.writeText(autoUrl);
                        alert('✅ Callback URL generated and copied to clipboard!');
                      }}
                      className="px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition-all whitespace-nowrap"
                    >
                      Auto Generate
                    </button>
                  </div>
                  <div className="mt-2 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                    <p className="text-xs text-gray-400 mb-1">📋 Copy this URL and paste it in your NOWPayments Dashboard:</p>
                    <p className="text-xs text-blue-400 font-mono break-all">
                      {nowpaymentsSettings.callback_url || `${window.location.origin}/api/webhooks/nowpayments`}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Go to: <a href="https://account.nowpayments.io/settings/ipn" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">NOWPayments IPN Settings</a> → Add this URL as IPN Callback URL
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-8">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="nowpayments_sandbox"
                      checked={nowpaymentsSettings.sandbox_mode}
                      onChange={(e) => setNowpaymentsSettings({ ...nowpaymentsSettings, sandbox_mode: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="nowpayments_sandbox" className="ml-2 text-gray-300">
                      Sandbox Mode (Testing)
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="nowpayments_enabled"
                      checked={nowpaymentsSettings.enabled}
                      onChange={(e) => setNowpaymentsSettings({ ...nowpaymentsSettings, enabled: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="nowpayments_enabled" className="ml-2 text-gray-300">
                      Enable NOWPayments
                    </label>
                  </div>
                </div>

                {nowpaymentsTestResult && (
                  <div className={`p-4 rounded-lg ${nowpaymentsTestResult.success ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {nowpaymentsTestResult.success ? '✅' : '❌'} {nowpaymentsTestResult.message}
                    {nowpaymentsTestResult.sandbox_mode && <span className="ml-2 text-yellow-400">(Sandbox Mode)</span>}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleTestNowpaymentsConnection}
                    disabled={testingNowpayments || !nowpaymentsSettings.api_key}
                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {testingNowpayments ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Testing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Test Connection
                      </>
                    )}
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save NOWPayments Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* SMTP Email Settings Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">SMTP Email Settings</h2>
                  <p className="text-gray-400 text-sm mt-1">Configure email notifications for order status updates</p>
                </div>
              </div>

              <form onSubmit={handleSaveSmtpSettings} className="space-y-6">
                {/* SMTP Host */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">
                      SMTP Host *
                    </label>
                    <input
                      type="text"
                      value={smtpSettings.smtp_host}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                      className="input-field"
                      placeholder="smtp.gmail.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">
                      SMTP Port *
                    </label>
                    <input
                      type="number"
                      value={smtpSettings.smtp_port}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) || 587 })}
                      className="input-field"
                      placeholder="587"
                      required
                    />
                  </div>
                </div>

                {/* SMTP Credentials */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">
                      SMTP Username *
                    </label>
                    <input
                      type="text"
                      value={smtpSettings.smtp_username}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_username: e.target.value })}
                      className="input-field"
                      placeholder="your-email@gmail.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">
                      SMTP Password *
                    </label>
                    <input
                      type="password"
                      value={smtpSettings.smtp_password}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                      className="input-field"
                      placeholder="App password or SMTP password"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      For Gmail, use App Password. <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 underline">Learn how to create one</a>
                    </p>
                  </div>
                </div>

                {/* From Email and Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">
                      From Email *
                    </label>
                    <input
                      type="email"
                      value={smtpSettings.smtp_from_email}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_from_email: e.target.value })}
                      className="input-field"
                      placeholder="noreply@yourdomain.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-300">
                      From Name
                    </label>
                    <input
                      type="text"
                      value={smtpSettings.smtp_from_name}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_from_name: e.target.value })}
                      className="input-field"
                      placeholder="Dexreaction"
                    />
                  </div>
                </div>

                {/* TLS Option */}
                <div className="flex items-center space-x-3 p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
                  <input
                    type="checkbox"
                    id="smtp_use_tls"
                    checked={smtpSettings.smtp_use_tls}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_use_tls: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                  />
                  <div>
                    <label htmlFor="smtp_use_tls" className="text-gray-300 font-medium cursor-pointer block">
                      Use TLS (STARTTLS)
                    </label>
                    <p className="text-xs text-gray-400 mt-1">
                      Enable for port 587. Disable for SSL on port 465.
                    </p>
                  </div>
                </div>

                {/* Enable SMTP */}
                <div className="flex items-center space-x-3 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <input
                    type="checkbox"
                    id="smtp_enabled"
                    checked={smtpSettings.smtp_enabled}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-green-500 focus:ring-green-500"
                  />
                  <div>
                    <label htmlFor="smtp_enabled" className="text-gray-300 font-medium cursor-pointer block">
                      Enable Email Notifications
                    </label>
                    <p className="text-xs text-gray-400 mt-1">
                      Send email notifications when guest order status changes (if customer provided email)
                    </p>
                  </div>
                </div>

                {/* Test Result */}
                {smtpTestResult && (
                  <div className={`p-4 rounded-lg border ${
                    smtpTestResult.success 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    <div className="flex items-start">
                      {smtpTestResult.success ? (
                        <svg className="w-6 h-6 text-green-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      <div>
                        <p className={`font-semibold ${smtpTestResult.success ? 'text-green-400' : 'text-red-400'}`}>
                          {smtpTestResult.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Info Box */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-white mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Common SMTP Settings
                  </h3>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• <strong>Gmail:</strong> smtp.gmail.com:587 (TLS) or smtp.gmail.com:465 (SSL)</li>
                    <li>• <strong>SendGrid:</strong> smtp.sendgrid.net:587</li>
                    <li>• <strong>Mailgun:</strong> smtp.mailgun.org:587</li>
                    <li>• <strong>AWS SES:</strong> email-smtp.[region].amazonaws.com:587</li>
                  </ul>
                </div>

                {/* Buttons */}
                <div className="flex flex-wrap gap-4 pt-4 border-t border-orange-500/20">
                  <button
                    type="button"
                    onClick={handleTestSmtpConnection}
                    disabled={testingSmtp || !smtpSettings.smtp_host || !smtpSettings.smtp_username}
                    className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center"
                  >
                    {testingSmtp ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Testing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send Test Email
                      </>
                    )}
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save SMTP Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Database Backup & Restore Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Database Backup & Restore</h2>
                  <p className="text-gray-400 text-sm mt-1">Create backups and restore your database</p>
                </div>
              </div>

              {/* Database Info */}
              {backupInfo && (
                <div className="bg-slate-700/50 rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-white mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Database Statistics
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{backupInfo.total_documents}</p>
                      <p className="text-xs text-gray-400">Total Documents</p>
                    </div>
                    {backupInfo.collections?.slice(0, 3).map((col) => (
                      <div key={col.name} className="bg-slate-800/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-white">{col.count}</p>
                        <p className="text-xs text-gray-400 capitalize">{col.name.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Backup Section */}
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-emerald-400 mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      Backup Database
                    </h3>
                    <p className="text-sm text-gray-400 mb-4">
                      Download a complete backup of your database including users, orders, settings, and all configurations.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleDownloadBackup}
                  disabled={backupLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center"
                >
                  {backupLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Creating Backup...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Backup
                    </>
                  )}
                </button>
              </div>

              {/* Restore Section */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
                <h3 className="font-bold text-amber-400 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Restore Database
                </h3>
                <p className="text-sm text-gray-400 mb-4">
                  ⚠️ <strong>Warning:</strong> Restoring will overwrite ALL existing data. Make sure to backup first!
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <input
                      type="file"
                      accept=".json"
                      onChange={(e) => setRestoreFile(e.target.files[0])}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-600 file:text-white file:cursor-pointer hover:file:bg-amber-700"
                    />
                  </div>
                  <button
                    onClick={handleRestoreBackup}
                    disabled={restoreLoading || !restoreFile}
                    className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {restoreLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Restoring...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Restore
                      </>
                    )}
                  </button>
                </div>

                {/* Restore Result */}
                {restoreResult && (
                  <div className={`mt-4 p-4 rounded-lg border ${
                    restoreResult.success 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    <div className="flex items-start">
                      {restoreResult.success ? (
                        <svg className="w-6 h-6 text-green-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      <div className="flex-1">
                        <p className={`font-semibold ${restoreResult.success ? 'text-green-400' : 'text-red-400'}`}>
                          {restoreResult.success ? 'Restore Successful!' : 'Restore Failed'}
                        </p>
                        {restoreResult.restored_collections?.length > 0 && (
                          <div className="mt-2 text-sm text-gray-400">
                            <p className="mb-1">Restored collections:</p>
                            <ul className="list-disc list-inside">
                              {restoreResult.restored_collections.map((col, idx) => (
                                <li key={idx}>{col.name}: {col.count} documents</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {restoreResult.errors?.length > 0 && (
                          <div className="mt-2 text-sm text-red-400">
                            <p className="mb-1">Errors:</p>
                            <ul className="list-disc list-inside">
                              {restoreResult.errors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Banner Promo Management Section */}
            <div className="reaction-card mt-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Banner Promo Management</h2>
                    <p className="text-gray-400 text-sm mt-1">Manage promotional banners on user dashboard</p>
                  </div>
                </div>
                <button
                  onClick={() => handleOpenBannerModal()}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Banner
                </button>
              </div>

              {/* Banner List */}
              {banners.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-400">No banners yet. Click "Add Banner" to create one.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {banners.map((banner) => (
                    <div key={banner.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <img 
                            src={banner.desktop_image_url} 
                            alt={banner.title}
                            className="w-32 h-auto rounded border border-slate-600"
                            onError={(e) => e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="50"%3E%3Crect fill="%23334155" width="100" height="50"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E'}
                          />
                          <div className="flex-1">
                            <h3 className="text-white font-semibold text-lg">{banner.title}</h3>
                            <p className="text-gray-400 text-sm mt-1 break-all">{banner.link_href}</p>
                            <div className="flex items-center space-x-4 mt-2">
                              <span className="text-xs text-gray-500">Order: {banner.order}</span>
                              <span className="text-xs text-gray-500">Clicks: {banner.click_count || 0}</span>
                              <span className={`text-xs px-2 py-1 rounded ${banner.enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                {banner.enabled ? 'Active' : 'Disabled'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleToggleBanner(banner.id)}
                            className={`px-3 py-2 rounded font-semibold transition-all ${banner.enabled ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'}`}
                          >
                            {banner.enabled ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => handleOpenBannerModal(banner)}
                            className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 px-3 py-2 rounded font-semibold transition-all"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteBanner(banner.id)}
                            className="bg-red-500/20 text-red-400 hover:bg-red-500/30 px-3 py-2 rounded font-semibold transition-all"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Banner Modal */}
            {showBannerModal && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" style={{ margin: 0 }}>
                <div className="bg-slate-900 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-purple-500/20">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-white">
                      {editingBanner ? 'Edit Banner' : 'Add New Banner'}
                    </h3>
                    <button onClick={handleCloseBannerModal} className="text-gray-400 hover:text-white">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <form onSubmit={handleSaveBanner} className="space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-300">
                        Banner Title *
                      </label>
                      <input
                        type="text"
                        value={bannerForm.title}
                        onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                        className="input-field"
                        placeholder="Summer Promo 2025"
                        required
                      />
                    </div>

                    {/* Desktop Image */}
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-300">
                        Desktop Image (1540x260) *
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUploadImage(e.target.files[0], 'desktop')}
                            className="hidden"
                            id="desktop-upload"
                          />
                          <label
                            htmlFor="desktop-upload"
                            className={`cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold transition-all ${uploadingDesktop ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {uploadingDesktop ? 'Uploading...' : 'Upload File'}
                          </label>
                          <span className="text-gray-400 text-sm">OR</span>
                        </div>
                        <input
                          type="url"
                          value={bannerForm.desktop_image_url}
                          onChange={(e) => setBannerForm({ ...bannerForm, desktop_image_url: e.target.value })}
                          className="input-field"
                          placeholder="https://example.com/desktop-banner.jpg"
                          required
                        />
                        {bannerForm.desktop_image_url && (
                          <img src={bannerForm.desktop_image_url} alt="Desktop preview" className="w-full h-auto rounded border border-slate-600" />
                        )}
                      </div>
                    </div>

                    {/* Mobile Image */}
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-300">
                        Mobile Image (600x290) *
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUploadImage(e.target.files[0], 'mobile')}
                            className="hidden"
                            id="mobile-upload"
                          />
                          <label
                            htmlFor="mobile-upload"
                            className={`cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold transition-all ${uploadingMobile ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {uploadingMobile ? 'Uploading...' : 'Upload File'}
                          </label>
                          <span className="text-gray-400 text-sm">OR</span>
                        </div>
                        <input
                          type="url"
                          value={bannerForm.mobile_image_url}
                          onChange={(e) => setBannerForm({ ...bannerForm, mobile_image_url: e.target.value })}
                          className="input-field"
                          placeholder="https://example.com/mobile-banner.jpg"
                          required
                        />
                        {bannerForm.mobile_image_url && (
                          <img src={bannerForm.mobile_image_url} alt="Mobile preview" className="w-64 h-auto rounded border border-slate-600" />
                        )}
                      </div>
                    </div>

                    {/* Link */}
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-300">
                        Banner Link (href) *
                      </label>
                      <input
                        type="url"
                        value={bannerForm.link_href}
                        onChange={(e) => setBannerForm({ ...bannerForm, link_href: e.target.value })}
                        className="input-field"
                        placeholder="https://example.com/promo"
                        required
                      />
                    </div>

                    {/* Order */}
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-300">
                        Display Order
                      </label>
                      <input
                        type="number"
                        value={bannerForm.order}
                        onChange={(e) => setBannerForm({ ...bannerForm, order: parseInt(e.target.value) || 0 })}
                        className="input-field"
                        min="0"
                      />
                      <p className="text-xs text-gray-400 mt-1">Lower numbers appear first in slider</p>
                    </div>

                    {/* Enable */}
                    <div className="flex items-center space-x-3 p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                      <input
                        type="checkbox"
                        id="banner_enabled"
                        checked={bannerForm.enabled}
                        onChange={(e) => setBannerForm({ ...bannerForm, enabled: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                      />
                      <label htmlFor="banner_enabled" className="text-gray-300 font-medium cursor-pointer">
                        Enable Banner
                      </label>
                    </div>

                    {/* Buttons */}
                    <div className="flex items-center space-x-3">
                      <button
                        type="submit"
                        disabled={loading || uploadingDesktop || uploadingMobile}
                        className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 disabled:from-gray-600 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed"
                      >
                        {loading ? 'Saving...' : (editingBanner ? 'Update Banner' : 'Create Banner')}
                      </button>
                      <button
                        type="button"
                        onClick={handleCloseBannerModal}
                        className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Unified Product Mappings Table */}
            <AdminPanelProductTable 
              productMappings={productMappings}
              setProductMappings={setProductMappings}
              handleSaveProductMapping={handleSaveProductMapping}
            />

            {/* Dexmoji Vendor Settings Section */}
            <div className="reaction-card">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Dexmoji Vendor Integration</h2>
                  <p className="text-gray-400 text-sm mt-1">Configure auto-fulfillment via Dexmoji API</p>
                </div>
              </div>

              <form onSubmit={handleSaveDexmojiSettings} className="space-y-6">
                {/* API Key */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Dexmoji API Key *
                  </label>
                  <input
                    type="text"
                    value={dexmojiSettings.api_key}
                    onChange={(e) => setDexmojiSettings({ ...dexmojiSettings, api_key: e.target.value })}
                    className="input-field"
                    placeholder="Enter your Dexmoji API key"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Get your API key from your Dexmoji vendor account
                  </p>
                </div>

                {/* Enable Integration */}
                <div className="flex items-center space-x-3 p-4 bg-teal-500/10 rounded-lg border border-teal-500/20">
                  <input
                    type="checkbox"
                    id="dexmoji_enabled"
                    checked={dexmojiSettings.enabled}
                    onChange={(e) => setDexmojiSettings({ ...dexmojiSettings, enabled: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-teal-500 focus:ring-teal-500"
                  />
                  <label htmlFor="dexmoji_enabled" className="text-gray-300 font-medium cursor-pointer">
                    Enable Dexmoji Integration
                  </label>
                </div>

                {/* Auto Fulfill */}
                <div className="flex items-center space-x-3 p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                  <input
                    type="checkbox"
                    id="dexmoji_auto_fulfill"
                    checked={dexmojiSettings.auto_fulfill}
                    onChange={(e) => setDexmojiSettings({ ...dexmojiSettings, auto_fulfill: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                    disabled={!dexmojiSettings.enabled}
                  />
                  <div>
                    <label htmlFor="dexmoji_auto_fulfill" className="text-gray-300 font-medium cursor-pointer block">
                      Auto-Fulfill Orders
                    </label>
                    <p className="text-xs text-gray-400 mt-1">
                      Automatically send completed orders to Dexmoji for fulfillment
                    </p>
                  </div>
                </div>

                {/* Test Connection Result */}
                {dexmojiConnectionResult && (
                  <div className={`p-4 rounded-lg border ${
                    dexmojiConnectionResult.success 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    <div className="flex items-center">
                      {dexmojiConnectionResult.success ? (
                        <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={dexmojiConnectionResult.success ? 'text-green-400' : 'text-red-400'}>
                        {dexmojiConnectionResult.message}
                      </span>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleTestDexmojiConnection}
                    disabled={testingDexmojiConnection || !dexmojiSettings.api_key}
                    className="btn-secondary flex-1"
                  >
                    {testingDexmojiConnection ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Testing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Test Connection
                      </>
                    )}
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex-1"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Dexmoji Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      {/* Reject Payment Modal */}
      {showRejectModal && (
        <div className="modal" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-red-500/20">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-red-400">Reject Payment</h2>
              </div>
              <button 
                onClick={() => setShowRejectModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-300 mb-4">
                Please provide a reason for rejecting this payment. The user will see this message.
              </p>
              
              <label className="block text-sm font-semibold mb-2 text-gray-300">Rejection Reason *</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="input-field resize-none"
                placeholder="e.g., Transaction signature invalid, amount mismatch, suspicious activity..."
                rows={4}
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleConfirmReject}
                disabled={!rejectReason.trim()}
                className="bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors flex-1"
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="btn-secondary px-6 py-3"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Deposit Modal */}
      {showRejectDepositModal && (
        <div className="modal" onClick={() => setShowRejectDepositModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-red-500/20">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-red-400">Reject Deposit</h2>
              </div>
              <button 
                onClick={() => setShowRejectDepositModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-300 mb-4">
                Please provide a reason for rejecting this deposit. The user will receive this notification.
              </p>
              
              <label className="block text-sm font-semibold mb-2 text-gray-300">Rejection Reason *</label>
              <textarea
                value={depositRejectReason}
                onChange={(e) => setDepositRejectReason(e.target.value)}
                className="input-field resize-none"
                placeholder="e.g., Invalid transaction signature, amount mismatch, duplicate transaction..."
                rows={4}
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleConfirmRejectDeposit}
                disabled={!depositRejectReason.trim()}
                className="bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors flex-1"
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => {
                  setShowRejectDepositModal(false);
                  setDepositRejectReason('');
                }}
                className="btn-secondary px-6 py-3"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Detail Modal */}
      {showDepositDetailModal && selectedDeposit && (
        <div className="modal" onClick={() => setShowDepositDetailModal(false)}>
          <div className="modal-content max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-purple-500/20 flex-shrink-0">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white">Deposit Details</h2>
              </div>
              <button 
                onClick={() => setShowDepositDetailModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-2" style={{maxHeight: 'calc(90vh - 120px)'}}>
              {/* Status Badge */}
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Status</span>
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                  selectedDeposit.status === 'completed' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                  selectedDeposit.status === 'verifying' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                  selectedDeposit.status === 'pending' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                  selectedDeposit.status === 'rejected' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                  'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                }`}>
                  {selectedDeposit.status === 'completed' && '✓ '}
                  {selectedDeposit.status === 'verifying' && '⏳ '}
                  {selectedDeposit.status === 'pending' && '⏱️ '}
                  {selectedDeposit.status === 'rejected' && '✗ '}
                  {selectedDeposit.status.toUpperCase()}
                </span>
              </div>

              {/* User Info */}
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">User Email</span>
                  <span className="text-white font-semibold">{selectedDeposit.user_email || 'Unknown'}</span>
                </div>
                {selectedDeposit.user_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">User ID</span>
                    <span className="text-gray-300 font-mono text-sm">{selectedDeposit.user_id}</span>
                  </div>
                )}
              </div>

              {/* Amount Info */}
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount (USD)</span>
                  <span className="text-2xl font-bold text-green-400">
                    ${selectedDeposit.amount.toFixed(2)}
                  </span>
                </div>
                {selectedDeposit.amount_sol && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Amount (SOL)</span>
                    <span className="text-white font-semibold font-mono">
                      {selectedDeposit.amount_sol.toFixed(5)} SOL
                    </span>
                  </div>
                )}
                {selectedDeposit.unique_identifier && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Unique Code</span>
                    <div className="flex items-center gap-2 px-3 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <span className="text-yellow-400 text-xs">🔢</span>
                      <span className="text-yellow-400 font-bold font-mono text-xl">
                        {selectedDeposit.unique_identifier}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Transaction Signature */}
              {selectedDeposit.transaction_signature && (
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-gray-400 text-sm">Transaction Signature</p>
                    <a
                      href={`https://solscan.io/tx/${selectedDeposit.transaction_signature}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-400 hover:text-purple-300 text-sm font-semibold flex items-center gap-1"
                    >
                      View on Solscan
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                  <code className="text-xs bg-slate-900 p-3 rounded text-purple-400 break-all block">
                    {selectedDeposit.transaction_signature}
                  </code>
                </div>
              )}

              {/* Balance Info */}
              {selectedDeposit.balance_before !== undefined && (
                <div className="p-4 bg-slate-800/50 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Balance Before</span>
                    <span className="text-white font-semibold">${selectedDeposit.balance_before.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Balance After</span>
                    <span className="text-white font-semibold">${selectedDeposit.balance_after.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedDeposit.status === 'rejected' && selectedDeposit.rejection_reason && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-semibold mb-2">Rejection Reason</p>
                  <p className="text-gray-300">{selectedDeposit.rejection_reason}</p>
                </div>
              )}

              {/* Dates */}
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Created</span>
                  <span className="text-gray-300">{new Date(selectedDeposit.created_at).toLocaleString()}</span>
                </div>
                {selectedDeposit.submitted_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Submitted</span>
                    <span className="text-gray-300">{new Date(selectedDeposit.submitted_at).toLocaleString()}</span>
                  </div>
                )}
                {selectedDeposit.verified_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Verified</span>
                    <span className="text-gray-300">{new Date(selectedDeposit.verified_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {selectedDeposit.status === 'verifying' && (
                <div className="flex gap-3 pt-4 border-t border-purple-500/20">
                  <button
                    onClick={() => {
                      setShowDepositDetailModal(false);
                      handleApproveDeposit(selectedDeposit.id);
                    }}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    ✓ Approve Deposit
                  </button>
                  <button
                    onClick={() => {
                      setShowDepositDetailModal(false);
                      handleRejectDeposit(selectedDeposit.id);
                    }}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    ✗ Reject Deposit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Process Order Modal */}
      {showProcessModal && (
        <div className="modal" onClick={() => !processingOrder && setShowProcessModal(false)}>
          <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            {/* Fixed Header */}
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-purple-500/20">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white">Process Order</h2>
              </div>
              <button 
                onClick={() => !processingOrder && setShowProcessModal(false)}
                disabled={processingOrder}
                className="text-gray-400 hover:text-white transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable Content Container */}
            <div className="max-h-[60vh] overflow-y-auto pr-2 mb-4" style={{scrollbarWidth: 'thin'}}>
              <div className="mb-6">
              <p className="text-gray-300 mb-4">
                Choose how you want to process this order:
              </p>

              {/* Reaction Info & Vendor Selector */}
              {orderVendorInfo && (
                <div className="mb-6 space-y-4">
                  {/* Reaction Info */}
                  <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{orderVendorInfo.reactionEmoji}</span>
                      <div className="flex-1">
                        <div className="text-sm text-gray-400 mb-1">Processing Reaction:</div>
                        <div className="font-bold text-white">{orderVendorInfo.reactionName}</div>
                      </div>
                    </div>
                  </div>

                  {/* Vendor Selection */}
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="text-sm font-semibold text-white mb-3">Select Fulfillment Vendor:</div>
                    <div className="space-y-3">
                      {/* Boostlegends Option */}
                      <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedVendor === 'boostlegends'
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-slate-700 hover:border-purple-500/50 bg-slate-800/50'
                      }`}>
                        <input
                          type="radio"
                          name="vendor"
                          value="boostlegends"
                          checked={selectedVendor === 'boostlegends'}
                          onChange={(e) => setSelectedVendor(e.target.value)}
                          className="w-5 h-5 text-purple-500 focus:ring-purple-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">Boostlegends</span>
                            {orderVendorInfo.boostlegendsId > 0 && (
                              <span className="text-xs text-purple-400">(ID: {orderVendorInfo.boostlegendsId})</span>
                            )}
                          </div>
                          {orderVendorInfo.boostlegendsId === 0 && (
                            <div className="text-xs text-red-400 mt-1">⚠️ Service ID not configured</div>
                          )}
                        </div>
                        {boostlegendsSettings?.enabled && (
                          <span className="text-xs text-green-400 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Enabled
                          </span>
                        )}
                      </label>

                      {/* Dexmoji Option */}
                      <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedVendor === 'dexmoji'
                          ? 'border-teal-500 bg-teal-500/20'
                          : 'border-slate-700 hover:border-teal-500/50 bg-slate-800/50'
                      }`}>
                        <input
                          type="radio"
                          name="vendor"
                          value="dexmoji"
                          checked={selectedVendor === 'dexmoji'}
                          onChange={(e) => setSelectedVendor(e.target.value)}
                          className="w-5 h-5 text-teal-500 focus:ring-teal-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">Dexmoji</span>
                            {orderVendorInfo.dexmojiId > 0 && (
                              <span className="text-xs text-teal-400">(ID: {orderVendorInfo.dexmojiId})</span>
                            )}
                          </div>
                          {orderVendorInfo.dexmojiId === 0 && (
                            <div className="text-xs text-red-400 mt-1">⚠️ Service ID not configured</div>
                          )}
                        </div>
                        {dexmojiSettings?.enabled && (
                          <span className="text-xs text-green-400 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Enabled
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Auto Process Option */}
              <button
                onClick={handleProcessAuto}
                disabled={processingOrder}
                className="w-full mb-4 p-6 bg-gradient-to-br from-purple-500/20 to-blue-500/20 hover:from-purple-500/30 hover:to-blue-500/30 border-2 border-purple-500/30 hover:border-purple-500/50 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-300 transition-colors">
                      🚀 Auto Process ({selectedVendor === 'dexmoji' ? 'Dexmoji' : 'Boostlegends'})
                    </h3>
                    <p className="text-sm text-gray-400 mb-2">
                      Automatically fulfill this order via {selectedVendor === 'dexmoji' ? 'Dexmoji' : 'Boostlegends'} API. Order will be completed immediately if successful.
                    </p>
                    <div className="flex items-center text-xs text-green-400">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Instant fulfillment
                    </div>
                  </div>
                </div>
              </button>

              {/* Manual Process Option */}
              <button
                onClick={handleProcessManual}
                disabled={processingOrder}
                className="w-full p-6 bg-slate-800/50 hover:bg-slate-800/70 border-2 border-slate-700 hover:border-slate-600 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                      👤 Manual Process
                    </h3>
                    <p className="text-sm text-gray-400 mb-2">
                      Set order to "Processing" status. You'll need to fulfill this order manually and click "Complete" when done.
                    </p>
                    <div className="flex items-center text-xs text-yellow-400">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Manual fulfillment required
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {processingOrder && (
              <div className="mb-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30 flex items-center">
                <svg className="animate-spin h-5 w-5 mr-3 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-blue-400">Processing order...</span>
              </div>
            )}
            </div>
            {/* End Scrollable Content */}

            {/* Fixed Footer - Cancel Button */}
            <div className="flex space-x-3 pt-4 border-t border-purple-500/20">
              <button
                onClick={() => setShowProcessModal(false)}
                disabled={processingOrder}
                className="flex-1 btn-secondary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {showUserDetailModal && selectedUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="admin-card max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold">User Detail</h2>
                <p className="text-gray-400">{selectedUser.email}</p>
              </div>
              <button
                onClick={() => {
                  setShowUserDetailModal(false);
                  setSelectedUser(null);
                  setUserDetail(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {loadingUserDetail ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-400 mt-2">Loading user details...</p>
              </div>
            ) : userDetail ? (
              <div className="space-y-6">
                {/* User Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Name</p>
                    <p className="text-white font-semibold">{userDetail.user?.full_name || 'N/A'}</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Balance</p>
                    <p className="text-green-400 font-semibold">${(userDetail.user?.balance || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Status</p>
                    {userDetail.user?.is_blocked ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-400">Blocked</span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">Active</span>
                    )}
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Support PIN</p>
                    <code className="text-purple-400 font-semibold">{userDetail.user?.support_pin}</code>
                  </div>
                </div>

                {/* Block/Unblock Button */}
                <div className="flex gap-4">
                  <button
                    onClick={() => handleBlockUser(selectedUser.id)}
                    className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                      userDetail.user?.is_blocked
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30'
                        : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                    }`}
                  >
                    {userDetail.user?.is_blocked ? '✓ Unblock User' : '⛔ Block User'}
                  </button>
                </div>

                {/* Custom Pricing Section */}
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Custom Pricing
                  </h3>
                  
                  {/* Current Custom Prices */}
                  {userCustomPrices.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-400 mb-2">Active Custom Prices:</p>
                      <div className="flex flex-wrap gap-2">
                        {userCustomPrices.map((cp) => {
                          const reaction = REACTION_TYPES.find(r => r.value === cp.reaction_type);
                          return (
                            <div key={cp.reaction_type} className="flex items-center gap-2 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-purple-500/20">
                              <span className="text-sm">{reaction?.label || cp.reaction_type}</span>
                              <span className="text-green-400 font-semibold">${cp.custom_rate}</span>
                              <button
                                onClick={() => handleDeleteCustomPrice(selectedUser.id, cp.reaction_type)}
                                className="text-red-400 hover:text-red-300 ml-1"
                                title="Delete custom price"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Set New Custom Price Form */}
                  <div className="flex flex-wrap gap-3 items-end">
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Reaction Type</label>
                      <select
                        value={customPriceForm.reaction_type}
                        onChange={(e) => setCustomPriceForm({...customPriceForm, reaction_type: e.target.value})}
                        className="bg-slate-800 border border-purple-500/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                      >
                        {REACTION_TYPES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Custom Rate ($)</label>
                      <input
                        type="number"
                        step="0.001"
                        min="0.001"
                        value={customPriceForm.custom_rate}
                        onChange={(e) => setCustomPriceForm({...customPriceForm, custom_rate: parseFloat(e.target.value) || 0})}
                        className="bg-slate-800 border border-purple-500/30 rounded-lg px-3 py-2 text-sm w-24 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <button
                      onClick={() => handleSetCustomPrice(selectedUser.id)}
                      className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-semibold border border-purple-500/30"
                    >
                      Set Price
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Default rate: $0.05 per reaction. Custom prices will apply to all new orders from this user.
                  </p>
                </div>

                {/* API Keys Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    API Keys ({userDetail.api_keys?.length || 0})
                  </h3>
                  {userDetail.api_keys?.length > 0 ? (
                    <div className="space-y-3">
                      {userDetail.api_keys.map((key) => (
                        <div key={key.id} className="bg-slate-800/50 p-4 rounded-lg border border-purple-500/20">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-white">{key.name || 'API Key'}</span>
                                {key.is_active ? (
                                  <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">Active</span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400">Disabled</span>
                                )}
                              </div>
                              <code className="text-xs text-gray-400 break-all">{key.api_key}</code>
                              {key.whitelisted_ips?.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1">IPs: {key.whitelisted_ips.join(', ')}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Created: {new Date(key.created_at).toLocaleString()}
                                {key.last_used_at && ` | Last used: ${new Date(key.last_used_at).toLocaleString()}`}
                              </p>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => handleToggleApiKey(selectedUser.id, key.id)}
                                className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                                  key.is_active
                                    ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                                    : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                }`}
                              >
                                {key.is_active ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                onClick={() => handleDeleteApiKey(selectedUser.id, key.id)}
                                className="px-3 py-1 rounded text-xs font-semibold bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                              >
                                Terminate
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No API keys found</p>
                  )}
                </div>

                {/* Orders Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Recent Orders ({userDetail.orders?.length || 0})
                  </h3>
                  {userDetail.orders?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-slate-700">
                            <th className="py-2 px-3">Type</th>
                            <th className="py-2 px-3">Qty</th>
                            <th className="py-2 px-3">Total</th>
                            <th className="py-2 px-3">Source</th>
                            <th className="py-2 px-3">Status</th>
                            <th className="py-2 px-3">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetail.orders.slice(0, 10).map((order) => (
                            <tr key={order.id} className="border-b border-slate-700/50">
                              <td className="py-2 px-3 capitalize">{order.reaction_type}</td>
                              <td className="py-2 px-3">{order.quantity}</td>
                              <td className="py-2 px-3 text-green-400">${order.total_usd?.toFixed(2)}</td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  order.source === 'api' 
                                    ? 'bg-purple-500/20 text-purple-400' 
                                    : order.source === 'telegram'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-green-500/20 text-green-400'
                                }`}>
                                  {order.source === 'api' ? '🔌 API' : order.source === 'telegram' ? '✈️ TG' : '🌐 Web'}
                                </span>
                              </td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                  order.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                  order.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                                  order.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                  'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {order.status}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-gray-400">{new Date(order.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No orders found</p>
                  )}
                </div>

                {/* Balance Transactions */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Balance Transactions ({userDetail.transactions?.length || 0})
                  </h3>
                  {userDetail.transactions?.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userDetail.transactions.slice(0, 10).map((tx) => (
                        <div key={tx.id} className="flex justify-between items-center bg-slate-800/50 p-3 rounded-lg text-sm">
                          <div>
                            <span className={`font-semibold ${tx.type === 'topup' ? 'text-green-400' : 'text-red-400'}`}>
                              {tx.type === 'topup' ? '+' : '-'}${tx.amount_usd?.toFixed(2)}
                            </span>
                            <span className="text-gray-400 ml-2">{tx.type}</span>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              tx.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                              tx.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {tx.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No transactions found</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">Failed to load user details</p>
            )}
          </div>
        </div>
      )}

      {/* Export Report Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="admin-card max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold flex items-center">
                  <svg className="w-6 h-6 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export Monthly Report
                </h2>
                <p className="text-gray-400 mt-1">Generate PDF report for transactions and user overview</p>
              </div>
              <button
                onClick={() => {
                  setShowExportModal(false);
                  setReportData(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Month/Year Filter */}
            <div className="bg-slate-800/50 p-4 rounded-lg mb-6">
              <h3 className="text-sm font-semibold text-gray-400 mb-3">Select Period</h3>
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Month</label>
                  <select
                    value={exportMonth}
                    onChange={(e) => setExportMonth(parseInt(e.target.value))}
                    className="bg-slate-800 border border-purple-500/30 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                  >
                    {[
                      'January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'
                    ].map((month, index) => (
                      <option key={index} value={index + 1}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Year</label>
                  <select
                    value={exportYear}
                    onChange={(e) => setExportYear(parseInt(e.target.value))}
                    className="bg-slate-800 border border-purple-500/30 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                  >
                    {[2023, 2024, 2025, 2026].map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleFetchReport}
                  disabled={loadingReport}
                  className="btn-primary px-6 py-2 disabled:opacity-50"
                >
                  {loadingReport ? 'Loading...' : 'Generate Report'}
                </button>
              </div>
            </div>

            {/* Report Preview */}
            {reportData && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 p-4 rounded-lg border border-purple-500/30">
                    <p className="text-gray-400 text-sm">Total Orders</p>
                    <p className="text-2xl font-bold text-white">{reportData.summary.total_orders}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-4 rounded-lg border border-blue-500/30">
                    <p className="text-gray-400 text-sm">Total Reactions</p>
                    <p className="text-2xl font-bold text-white">{reportData.summary.total_reactions.toLocaleString()}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 p-4 rounded-lg border border-green-500/30">
                    <p className="text-gray-400 text-sm">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-400">${reportData.summary.total_revenue.toFixed(2)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500/20 to-yellow-500/20 p-4 rounded-lg border border-orange-500/30">
                    <p className="text-gray-400 text-sm">Active Users</p>
                    <p className="text-2xl font-bold text-white">{reportData.summary.total_users}</p>
                  </div>
                </div>

                {/* Reaction Breakdown */}
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">Reaction Breakdown</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(reportData.summary.reaction_breakdown).map(([type, count]) => {
                      const emojis = { rocket: '🚀', fire: '🔥', poop: '💩', redflag: '🚩', favorite: '⭐' };
                      const isPopular = type === reportData.summary.most_popular_reaction.type;
                      return (
                        <div 
                          key={type} 
                          className={`px-4 py-2 rounded-lg border ${isPopular ? 'bg-purple-500/20 border-purple-500' : 'bg-slate-700/50 border-slate-600'}`}
                        >
                          <span className="mr-2">{emojis[type]}</span>
                          <span className="capitalize">{type}</span>
                          <span className="ml-2 font-bold">{count.toLocaleString()}</span>
                          {isPopular && <span className="ml-2 text-xs text-purple-400">👑 Most Popular</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top Users Preview */}
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">Top 5 Users by Spending</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-slate-700">
                          <th className="py-2 px-3">#</th>
                          <th className="py-2 px-3">Email</th>
                          <th className="py-2 px-3">Orders</th>
                          <th className="py-2 px-3">Reactions</th>
                          <th className="py-2 px-3">Total Spent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.users.slice(0, 5).map((user, index) => (
                          <tr key={user.user_id} className="border-b border-slate-700/50">
                            <td className="py-2 px-3">{index + 1}</td>
                            <td className="py-2 px-3">{user.email}</td>
                            <td className="py-2 px-3">{user.total_orders}</td>
                            <td className="py-2 px-3">{user.total_reactions.toLocaleString()}</td>
                            <td className="py-2 px-3 text-green-400 font-semibold">${user.total_spent.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Export Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleExportPDF}
                    className="btn-primary px-8 py-3 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download PDF Report</span>
                  </button>
                </div>
              </div>
            )}

            {!reportData && !loadingReport && (
              <div className="text-center py-12 text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>Select a month and year, then click "Generate Report" to preview data</p>
              </div>
            )}

            {loadingReport && (
              <div className="text-center py-12">
                <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-400">Generating report...</p>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPanel;
